/**
 * Textes ajoutés pour l’adaptation au segment de l’établissement (éducation / entreprise) et
 * pour les écrans corrigés (lecteur d’évaluation, partage, profil, import, évaluations de classe).
 *
 * Vocabulaire scolaire : clés suffixées `_education`, lues avec le mécanisme de contexte d’i18next
 * (`t(key, { context })` avec `context` fourni par `useInstitutionSegment()`) ; sans contexte, la clé de base.
 * Clés plates. fr / en sont chargés au démarrage ; les autres langues sont fusionnées dans leur bundle
 * chargé à la demande (`lazySlices` dans `lib/i18n.ts`). Les clés absentes d’une langue retombent sur l’anglais.
 */
export type EducationSegmentLocaleCode = 'fr' | 'en' | 'de' | 'es' | 'it' | 'pt' | 'lb' | 'uk' | 'ru' | 'ar' | 'tr';

export const educationSegmentLocales: Record<EducationSegmentLocaleCode, Record<string, string>> = {
  fr: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Action refusée : vos droits ne permettent pas de gérer ce compte. Contactez un administrateur de l’établissement.",
    "institution.designers.readOnlyNotice": "Consultation seule : la gestion des comptes est réservée aux administrateurs de l’établissement.",
    "nav.learners_education": "Élèves",
    "nav.eduflowList_education": "Cours",
    "institution.tabs.flows_education": "Cours",
    "institution.tabs.learners_education": "Élèves",
    "institution.tabs.designers_education": "Professeurs",
    "institution.tabs.teachers": "Professeurs",
    "institution.tabs.learnerAnalytics_education": "Analyse des élèves",
    "institution.nav.section.people_education": "Équipes & élèves",
    "institution.nav.section.content_education": "Contenus & cours",
    "institution.rhNav.requests_education": "Demandes de contenus",
    "rh.requests.title_education": "Demandes de contenus",
    "rh.requests.subtitle_education": "Demandes des enseignants et validations",
    "header.trainingContentRequest": "Demande de formation (contenus)",
    "header.trainingContentRequest_education": "Demande de contenu",
    "dashboard.totalLearners_education": "Total élèves",
    "dashboard.publishedTraining_education": "Cours publiés",
    "dashboard.collaborators": "Collaborateurs",
    "dashboard.collaborators_education": "Équipe pédagogique",
    "institution.dashboard.registrationCodeLabel": "Codes d’inscription",
    "institution.dashboard.registrationCodeLearner": "Apprenant",
    "institution.dashboard.registrationCodeLearner_education": "Élève",
    "institution.dashboard.registrationCodeTrainer": "Formateur (professeur)",
    "institution.dashboard.registrationCodeTrainer_education": "Enseignant",
    "institution.dashboard.registrationCodeLead": "Responsable formation (professeur principal)",
    "institution.dashboard.registrationCodeLead_education": "Direction",
    "institution.dashboard.registrationCodeCopy": "Copier",
    "institution.dashboard.registrationCodeCopied": "Copié",
    "institution.dashboard.registrationCodeHelp": "Communiquez le code qui correspond au type de compte. Un compte ne peut pas être créé sans lui.",
    "institution.flows.title_education": "Cours",
    "institution.flows.subtitle_education": "Gérez vos cours",
    "institution.flows.statTotal_education": "Total cours",
    "institution.flows.statLearners_education": "Total élèves",
    "institution.flows.searchPh_education": "Rechercher un cours…",
    "institution.flows.empty_education": "Aucun cours trouvé",
    "institution.flows.badgeEnterprise_education": "Demande de contenu",
    "institution.flows.deleteEnterpriseTooltip_education": "Mettre en corbeille (demande de contenu)",
    "institution.flows.deleteEnterpriseTitle_education": "Retirer ce cours issu d’une demande de contenu ?",
    "institution.flows.deleteEnterpriseHint_education": "Le cours sera mis en corbeille (suppression logique). Les enseignants ne peuvent pas effectuer cette action ; seule la direction de l’établissement peut le restaurer ou le retirer depuis ce tableau.",
    "institution.flows.deleteEnterpriseSuccess_education": "Cours mis en corbeille.",
    "role.student": "Élève",
    "role.student_education": "Élève",
    "role.learner_education": "Élève",
    "role.teacher_education": "Enseignant",
    "institution.designers.roleTeacher_education": "Enseignant",
    "institution.designers.roleOptFormateur_education": "Enseignant",
    "institution.designers.roleTrainingLead_education": "Prof. principal",
    "institution.designers.groupTeacher": "Formateurs",
    "institution.designers.groupTeacher_education": "Enseignants",
    "institution.designers.groupTrainingLead": "Responsables formation",
    "institution.designers.groupTrainingLead_education": "Professeurs principaux",
    "institution.designers.inviteTeacher": "Inviter un professeur",
    "institution.designers.inviteTeacherTitle": "Inviter un professeur",
    "institution.designers.inviteTeacherHelp": "Enseignant : enseigne dans les classes. Professeur principal : suit une classe.",
    "institution.designers.statTeachers": "Professeurs",
    "institution.designers.teachersSubtitle": "Enseignants et professeurs principaux de l’établissement.",
    "institution.learners.title_education": "Élèves",
    "institution.learners.statLearners_education": "Élèves",
    "institution.learners.subtitle_education": "{{count}} élève(s) inscrit(s) dans votre établissement",
    "institution.learners.edu.courses": "Cours",
    "institution.learners.edu.coursesDone": "Cours terminés",
    "institution.learners.edu.classTitle": "Classe",
    "institution.learners.edu.classHint": "La classe se gère dans l’onglet Classes.",
    "institution.learners.edu.noClass": "Aucune classe",
    "institution.learners.edu.profileTitle": "Informations de l’élève",
    "institution.learners.edu.profileHint": "Genre, date de naissance et diplôme de l’élève.",
    "institution.learners.edu.diploma": "Diplôme obtenu",
    "institution.learners.edu.saveProfile": "Enregistrer la fiche",
    "institution.menuBlocked.segmentBody": "Cette fonctionnalité n’est pas proposée pour votre type d’établissement.",
    "pages.learners.sortRecentPage": "Dernière activité (page affichée)",
    "pages.learners.sortProgressPage": "Progression (page affichée)",
    "pages.learners.statsPageHint": "En cours, Terminé et Progression moy. : calculés sur les {{count}} personnes de la page affichée.",
    "pages.learners.exporting": "Export en cours…",
    "pages.learners.exportError": "Export impossible. Réessayez.",
    "institution.learners.importMissingFields": "Prénom, nom et e-mail sont obligatoires.",
    "institution.learners.importBadDate": "Date illisible : utilisez JJ/MM/AAAA ou AAAA-MM-JJ.",
    "institution.learners.importNoRows": "Aucune ligne importable trouvée.",
    "institution.learners.importResult": "{{count}} compte(s) créé(s).",
    "institution.learners.downloadTemplate": "Modèle CSV",
    "institution.classes.noAssignedFlows": "Aucun cours n’est encore assigné à cette classe. Choisissez-en un ci-dessus.",
    "institution.evaluations.pointsRequired": "Saisissez une note avant de valider.",
    "institution.evaluations.pointsRange": "La note doit être comprise entre 0 et {{max}}.",
    "institution.evaluations.pointsInvalid": "Note invalide.",
    "institution.evaluations.points": "Note",
    "institution.evaluations.saveError": "Enregistrement de la note impossible.",
    "institution.evaluations.settingsSaveError": "Enregistrement des règles impossible.",
    "institution.evaluations.applyLevelError": "Mise à jour du niveau impossible.",
    "institution.evaluations.downloadFile": "Télécharger le fichier",
    "institution.evaluations.fileDownloadError": "Téléchargement du fichier impossible.",
    "institution.learners.importDateIgnored": "{{field}} illisible, ignorée (JJ/MM/AAAA ou AAAA-MM-JJ).",
    "institution.learners.importFieldBirthDate": "Date de naissance",
    "institution.learners.importFieldHireDate": "Date d’embauche",
    "institution.learners.importFieldArrivalDate": "Date d’arrivée",
    "institution.learners.importWarnings": "{{count}} avertissement(s) (ligne importée) :",
    "player.evaluation.type.text": "Texte libre",
    "player.evaluation.type.singleChoice": "Choix unique",
    "player.evaluation.type.multipleChoice": "Choix multiples",
    "player.evaluation.type.dropdown": "Liste déroulante",
    "player.evaluation.type.numeric": "Numérique",
    "player.evaluation.type.fileUpload": "Fichier",
    "player.evaluation.requiredAll": "Veuillez répondre à toutes les questions obligatoires.",
    "player.evaluation.requiredOne": "Veuillez répondre à cette question pour continuer.",
    "player.evaluation.emptyAnswer": "Réponse vide.",
    "player.evaluation.openNoReference": "Question ouverte sans référence.",
    "player.evaluation.textNeedsSession": "La correction des réponses texte nécessite un parcours publié et une session connectée.",
    "player.evaluation.invalidServerScore": "Réponse serveur invalide pour la notation texte.",
    "player.evaluation.submitError": "Erreur lors de l’envoi.",
    "player.evaluation.passReached": "Seuil de réussite atteint.",
    "player.evaluation.passNotReached": "Seuil de réussite non atteint.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} pts ({{threshold}} % requis)",
    "player.evaluation.questionNumber": "Question {{num}}",
    "player.evaluation.question": "Question",
    "player.evaluation.untitled": "Sans intitulé",
    "player.evaluation.toReview": "À corriger",
    "player.evaluation.points": "{{earned}} / {{max}} pts",
    "player.evaluation.semanticScore": "Score sémantique : {{score}} %",
    "player.evaluation.thanks": "Merci !",
    "player.evaluation.answersSaved": "Vos réponses ont été enregistrées.",
    "player.evaluation.questionCount": "{{count}} question(s)",
    "player.evaluation.start": "Commencer",
    "player.evaluation.questionProgress": "Question {{num}} / {{total}}",
    "player.evaluation.required": "Obligatoire",
    "player.evaluation.chooseAnswer": "Choisir une réponse…",
    "player.evaluation.answerPlaceholder": "Saisissez votre réponse…",
    "player.evaluation.chooseFile": "Choisir un fichier",
    "player.evaluation.replaceFile": "Remplacer le fichier",
    "player.evaluation.fileHint": "PDF, image, document, texte, audio ou vidéo — {{max}} Mo maximum.",
    "player.evaluation.fileTooLarge": "Fichier trop volumineux (maximum {{max}} Mo).",
    "player.evaluation.fileBadType": "Type de fichier non accepté.",
    "player.evaluation.fileNeedsSession": "Le dépôt de fichier nécessite un parcours publié et une session connectée.",
    "player.evaluation.fileUploadError": "Envoi du fichier impossible. Réessayez.",
    "player.evaluation.fileUploading": "Envoi de {{name}}… {{progress}} %",
    "player.evaluation.fileSent": "Fichier envoyé : {{name}}",
    "player.evaluation.removeFile": "Retirer le fichier",
    "player.evaluation.fileStillUploading": "Patientez : un fichier est encore en cours d’envoi.",
    "player.evaluation.fileToReview": "Fichier transmis : il sera corrigé par l’enseignant.",
    "player.evaluation.back": "Retour",
    "player.evaluation.grading": "Correction…",
    "player.evaluation.finish": "Terminer",
    "player.evaluation.next": "Suivant",
    "player.evaluation.noQuestions": "Aucune question n’est configurée pour cette évaluation.",
    "player.evaluation.continue": "Continuer",
    "player.evaluation.pendingReview": "Résultat provisoire : certaines réponses seront corrigées par l’enseignant.",
    "player.evaluation.textToReview": "Réponse transmise : elle sera corrigée par l’enseignant.",
    "share.audience.loading": "Chargement…",
    "share.audience.serverError": "Erreur serveur ({{status}})",
    "share.audience.networkError": "Erreur réseau",
    "share.audience.share": "Partager",
    "share.audience.unshare": "Retirer le partage",
    "share.audience.noResult": "Aucun résultat.",
    "share.audience.allShared": "Tout est déjà partagé.",
    "share.audience.members": "{{count}} membre(s)",
    "share.audience.members_one": "{{count}} membre",
    "share.audience.members_other": "{{count}} membres",
    "share.audience.classShared": "{{count}} classe(s) partagée(s)",
    "share.audience.groupShared": "{{count}} groupe(s) partagé(s)",
    "share.audience.serviceShared": "{{count}} service(s) partagé(s)",
    "share.audience.titleClass": "Partager à une classe",
    "share.audience.titleService": "Partager à un service",
    "share.audience.titleGroup": "Partager à un groupe",
    "share.audience.noClassShared": "Aucune classe partagée.",
    "share.audience.noServiceShared": "Aucun service partagé.",
    "share.audience.noGroupShared": "Aucun groupe partagé.",
    "share.audience.searchClass": "Rechercher une classe…",
    "share.audience.searchService": "Rechercher un service…",
    "share.audience.searchGroup": "Rechercher un groupe…",
    "share.audience.emptyClass": "Aucune classe. Créez des classes dans le tableau de bord établissement.",
    "share.audience.emptyService": "Aucun service. Créez des services dans le tableau de bord établissement.",
    "share.audience.emptyGroup": "Aucun groupe trouvé. Créez des groupes dans le tableau de bord établissement.",
    "share.audience.assignedClass": "{{count}} élève(s) ajouté(s) sur {{total}} membre(s) de la classe « {{name}} ».",
    "share.audience.assignedService": "{{count}} apprenant(s) ajouté(s) sur {{total}} membre(s) du service « {{name}} ».",
    "share.audience.assignedGroup": "{{count}} apprenant(s) ajouté(s) sur {{total}} membre(s) du groupe « {{name}} ».",
    "share.audience.shareError": "Partage impossible.",
    "share.audience.unshareClassError": "Impossible de retirer le partage de classe.",
    "share.audience.unshareGroupError": "Impossible de retirer le partage du groupe.",
    "profile.page.eyebrow": "Espace personnel",
    "profile.page.title": "Mon profil",
    "profile.page.subtitle": "Centralisez vos informations, votre sécurité et vos préférences.",
    "profile.page.accountStatus": "État du compte",
    "profile.page.active": "Actif",
    "profile.page.loading": "Chargement du profil…",
    "profile.page.loadError": "Impossible de charger le profil",
    "profile.page.position": "Poste",
    "profile.page.position_education": "Fonction",
    "profile.page.service": "Service",
    "profile.page.department": "Département",
    "profile.page.location": "Localisation",
    "profile.page.tabProfile": "Informations personnelles",
    "profile.page.tabSecurity": "Sécurité du compte",
    "profile.page.tabPreferences": "Notifications & langue",
    "profile.page.profileHint": "Mettez à jour vos informations de contact et de poste.",
    "profile.page.profileHint_education": "Mettez à jour vos informations de contact.",
    "profile.page.firstName": "Prénom",
    "profile.page.lastName": "Nom",
    "profile.page.email": "E-mail",
    "profile.page.phone": "Téléphone",
    "profile.page.city": "Site / Ville",
    "profile.page.city_education": "Ville",
    "profile.page.country": "Pays",
    "profile.page.saving": "Enregistrement…",
    "profile.page.save": "Enregistrer",
    "profile.page.saved": "Profil mis à jour",
    "profile.page.saveError": "Impossible de mettre à jour le profil",
    "profile.page.securityHint": "Choisissez un mot de passe fort et unique.",
    "profile.page.passwordMismatch": "La confirmation du mot de passe ne correspond pas",
    "profile.page.passwordChanged": "Mot de passe modifié",
    "profile.page.passwordError": "Impossible de modifier le mot de passe",
    "profile.page.preferencesSaved": "Préférences enregistrées",
    "profile.page.preferencesError": "Impossible d’enregistrer les préférences",
    "institution.title": "Établissement",
    "institution.designers.assignTeam": "Équipe du manager",
    "institution.designers.assignTeamEmpty": "Aucun formateur ni responsable formation à assigner.",
    "institution.designers.assignTeamHelp": "Cochez les formateurs et responsables formation rattachés à ce manager.",
    "institution.designers.cannotRemoveSelf": "Vous ne pouvez pas retirer votre propre compte ici.",
    "institution.designers.colClasses": "Classes",
    "institution.designers.deleteTitle": "Retirer ce collaborateur ?",
    "institution.designers.groupAdmin": "Administrateurs",
    "institution.designers.groupEmpty": "Aucun compte dans ce groupe",
    "institution.designers.groupManager": "Managers",
    "institution.designers.groupRhDrh": "RH / DRH",
    "institution.designers.learnerFormHint": "L’e-mail de connexion n’est pas modifiable ici. Cochez les groupes ci-dessous puis enregistrez la fiche. Service = référentiel établissement.",
    "institution.designers.learnerFullProfileTitle": "Fiche apprenant complète",
    "institution.designers.learnerNamesFromHeaderHint": "Le prénom et le nom sont ceux du haut de la fiche (compte collaborateur) : ils seront appliqués à la fiche apprenant à l’enregistrement.",
    "institution.designers.managerTeam": "Équipe du manager",
    "institution.designers.noManager": "Aucun manager",
    "institution.designers.roleDropdownHint": "Liste déroulante : cliquez pour afficher tous les rôles.",
    "institution.designers.saveFail": "Enregistrement impossible",
    "institution.designers.saveTeam": "Enregistrer l’équipe",
    "institution.designers.sheetEmailNote": "L’adresse e-mail de connexion ne peut pas être modifiée ici.",
    "institution.designers.sheetTitle": "Fiche collaborateur",
    "institution.designers.teamNone": "Aucune personne assignée. Cochez des formateurs ou responsables formation ci-dessous.",
    "institution.learners.companyArrivalDate": "Date d’arrivée dans l’entreprise",
    "institution.learners.groupsPickHint": "Cochez un ou plusieurs groupes, puis enregistrez. La liste provient de l’onglet « Groupes ».",
    "institution.learners.groupsSaveError": "Impossible d’enregistrer les groupes.",
    "institution.learners.groupsSaved": "Groupes enregistrés.",
    "institution.learners.infpc.ageFromBirthHint": "Calculé automatiquement à partir de la date de naissance.",
    "institution.learners.infpc.invalidNumber": "Nombre invalide (ancienneté)",
    "institution.learners.infpc.isSalariePlus": "Salarié+",
    "institution.learners.lastActive": "Dernière activité",
    "institution.learners.saveGroups": "Enregistrer les groupes",
    "institution.learners.importResult_one": "{{count}} compte créé.",
    "institution.learners.importResult_other": "{{count}} comptes créés.",
    "player.evaluation.questionCount_one": "{{count}} question",
    "player.evaluation.questionCount_other": "{{count}} questions",
    "share.audience.classShared_one": "{{count}} classe partagée",
    "share.audience.classShared_other": "{{count}} classes partagées",
    "share.audience.groupShared_one": "{{count}} groupe partagé",
    "share.audience.groupShared_other": "{{count}} groupes partagés",
    "share.audience.serviceShared_one": "{{count}} service partagé",
    "share.audience.serviceShared_other": "{{count}} services partagés",
    "institution.menuBlocked.body": "Cette section a été désactivée pour votre établissement par l’administrateur de la plateforme. Si vous pensez qu’il s’agit d’une erreur, contactez votre administrateur.",
    "profile.changeAvatar": "Changer la photo de profil",
    "profile.avatarInvalidType": "Veuillez sélectionner une image (JPEG, PNG, WebP, GIF).",
    "profile.avatarUpdated": "Photo de profil mise à jour",
    "profile.avatarUploadFailed": "Impossible de mettre à jour la photo de profil",
    "institution.evaluations.subtitle_education": "Résultats d’une classe sur un cours. Les sondages et l’auto-évaluation ne comptent pas.",
    "institution.evaluations.chooseFlow_education": "Choisir un cours",
    "institution.evaluations.emptyFlow_education": "Aucun cours joué par cette classe.",
    "institution.evaluations.emptyLearners_education": "Cette classe n’a pas encore d’élèves.",
    "institution.evaluations.learner_education": "Élève",
    "institution.evaluations.comment_education": "Commentaire pour l’élève",
    "institution.classes.flowsTitle_education": "Cours de la classe",
    "institution.classes.flowsHint_education": "Assignez un cours publié à toute la classe. Les élèves déjà inscrits ne sont pas dupliqués.",
    "institution.classes.chooseFlow_education": "Choisir un cours publié",
    "institution.classes.noFlows_education": "Aucun cours publié disponible pour cet établissement.",
    "institution.classes.subtitle_education": "Une classe a un professeur principal, des enseignants et des élèves. Importez la liste des élèves et assignez-leur des cours en une fois.",
    "institution.classes.lead_education": "Professeur principal",
    "institution.classes.leadPlaceholder_education": "Choisir un professeur principal",
    "institution.classes.leadHint_education": "Le professeur principal suit la classe (ou un coordinateur, un membre de la direction). Les enseignants y enseignent leurs matières.",
    "institution.classes.leadMissing_education": "Aucun professeur principal",
    "institution.classes.noLead_education": "Créez d’abord un compte enseignant, professeur principal ou administrateur dans Professeurs.",
    "institution.classes.trainers_education": "Enseignants",
    "institution.classes.learners_education": "Élèves",
    "institution.classes.addTrainer_education": "Ajouter un enseignant",
    "institution.classes.addLearner_education": "Ajouter un élève",
    "institution.classes.noTrainers_education": "Aucun enseignant dans cette classe.",
    "institution.classes.noLearners_education": "Aucun élève dans cette classe.",
    "institution.classes.noTrainerAvailable_education": "Aucun enseignant disponible.",
    "institution.classes.noLearnerAvailable_education": "Aucun élève disponible.",
  },
  en: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Action denied: your rights do not allow you to manage this account. Contact an administrator of your organisation.",
    "institution.designers.readOnlyNotice": "View only: account management is reserved for the organisation’s administrators.",
    "nav.learners_education": "Students",
    "nav.eduflowList_education": "Courses",
    "institution.tabs.flows_education": "Courses",
    "institution.tabs.learners_education": "Students",
    "institution.tabs.designers_education": "Teachers",
    "institution.tabs.teachers": "Teachers",
    "institution.tabs.learnerAnalytics_education": "Student analytics",
    "institution.nav.section.people_education": "Staff & students",
    "institution.nav.section.content_education": "Content & courses",
    "institution.rhNav.requests_education": "Content requests",
    "rh.requests.title_education": "Content requests",
    "rh.requests.subtitle_education": "Teacher requests and approvals",
    "header.trainingContentRequest": "Training request (content)",
    "header.trainingContentRequest_education": "Content request",
    "dashboard.totalLearners_education": "Total students",
    "dashboard.publishedTraining_education": "Published courses",
    "dashboard.collaborators": "Staff accounts",
    "dashboard.collaborators_education": "Teaching staff",
    "institution.dashboard.registrationCodeLabel": "Sign-up codes",
    "institution.dashboard.registrationCodeLearner": "Learner",
    "institution.dashboard.registrationCodeLearner_education": "Student",
    "institution.dashboard.registrationCodeTrainer": "Trainer (teacher)",
    "institution.dashboard.registrationCodeTrainer_education": "Teacher",
    "institution.dashboard.registrationCodeLead": "Training lead (head teacher)",
    "institution.dashboard.registrationCodeLead_education": "Leadership team",
    "institution.dashboard.registrationCodeCopy": "Copy",
    "institution.dashboard.registrationCodeCopied": "Copied",
    "institution.dashboard.registrationCodeHelp": "Share the code that matches the account type. An account cannot be created without it.",
    "institution.flows.title_education": "Courses",
    "institution.flows.subtitle_education": "Manage your courses",
    "institution.flows.statTotal_education": "Total courses",
    "institution.flows.statLearners_education": "Total students",
    "institution.flows.searchPh_education": "Search a course…",
    "institution.flows.empty_education": "No course found",
    "institution.flows.badgeEnterprise_education": "Content request",
    "institution.flows.deleteEnterpriseTooltip_education": "Move to trash (content request)",
    "institution.flows.deleteEnterpriseTitle_education": "Remove this course created from a content request?",
    "institution.flows.deleteEnterpriseHint_education": "The course will be moved to the trash (soft delete). Teachers cannot do this; only the school leadership can restore or remove it from this dashboard.",
    "institution.flows.deleteEnterpriseSuccess_education": "Course moved to trash.",
    "role.student": "Student",
    "role.student_education": "Student",
    "role.learner_education": "Student",
    "role.teacher_education": "Teacher",
    "institution.designers.roleTeacher_education": "Teacher",
    "institution.designers.roleOptFormateur_education": "Teacher",
    "institution.designers.roleTrainingLead_education": "Head teacher",
    "institution.designers.groupTeacher": "Trainers",
    "institution.designers.groupTeacher_education": "Teachers",
    "institution.designers.groupTrainingLead": "Training leads",
    "institution.designers.groupTrainingLead_education": "Head teachers",
    "institution.designers.inviteTeacher": "Invite a teacher",
    "institution.designers.inviteTeacherTitle": "Invite a teacher",
    "institution.designers.inviteTeacherHelp": "Teacher: teaches in classes. Head teacher: follows a class.",
    "institution.designers.statTeachers": "Teachers",
    "institution.designers.teachersSubtitle": "Teachers and head teachers of the school.",
    "institution.learners.title_education": "Students",
    "institution.learners.statLearners_education": "Students",
    "institution.learners.subtitle_education": "{{count}} student(s) enrolled in your school",
    "institution.learners.edu.courses": "Courses",
    "institution.learners.edu.coursesDone": "Completed courses",
    "institution.learners.edu.classTitle": "Class",
    "institution.learners.edu.classHint": "The class is managed in the Classes tab.",
    "institution.learners.edu.noClass": "No class",
    "institution.learners.edu.profileTitle": "Student information",
    "institution.learners.edu.profileHint": "Student’s gender, date of birth and diploma.",
    "institution.learners.edu.diploma": "Diploma obtained",
    "institution.learners.edu.saveProfile": "Save the record",
    "institution.menuBlocked.segmentBody": "This feature is not available for your type of organisation.",
    "pages.learners.sortRecentPage": "Last activity (current page)",
    "pages.learners.sortProgressPage": "Progress (current page)",
    "pages.learners.statsPageHint": "In progress, Completed and Avg. progress: computed on the {{count}} people of the current page.",
    "pages.learners.exporting": "Exporting…",
    "pages.learners.exportError": "Export failed. Please try again.",
    "institution.learners.importMissingFields": "First name, last name and email are required.",
    "institution.learners.importBadDate": "Unreadable date: use DD/MM/YYYY or YYYY-MM-DD.",
    "institution.learners.importNoRows": "No importable row found.",
    "institution.learners.importResult": "{{count}} account(s) created.",
    "institution.learners.downloadTemplate": "CSV template",
    "institution.classes.noAssignedFlows": "No course is assigned to this class yet. Choose one above.",
    "institution.evaluations.pointsRequired": "Enter a mark before validating.",
    "institution.evaluations.pointsRange": "The mark must be between 0 and {{max}}.",
    "institution.evaluations.pointsInvalid": "Invalid mark.",
    "institution.evaluations.points": "Mark",
    "institution.evaluations.saveError": "The mark could not be saved.",
    "institution.evaluations.settingsSaveError": "The rules could not be saved.",
    "institution.evaluations.applyLevelError": "The level could not be updated.",
    "institution.evaluations.downloadFile": "Download the file",
    "institution.evaluations.fileDownloadError": "The file could not be downloaded.",
    "institution.learners.importDateIgnored": "{{field}} unreadable, ignored (DD/MM/YYYY or YYYY-MM-DD).",
    "institution.learners.importFieldBirthDate": "Date of birth",
    "institution.learners.importFieldHireDate": "Hire date",
    "institution.learners.importFieldArrivalDate": "Start date",
    "institution.learners.importWarnings": "{{count}} warning(s) (row imported):",
    "player.evaluation.type.text": "Free text",
    "player.evaluation.type.singleChoice": "Single choice",
    "player.evaluation.type.multipleChoice": "Multiple choice",
    "player.evaluation.type.dropdown": "Drop-down list",
    "player.evaluation.type.numeric": "Numeric",
    "player.evaluation.type.fileUpload": "File",
    "player.evaluation.requiredAll": "Please answer all required questions.",
    "player.evaluation.requiredOne": "Please answer this question to continue.",
    "player.evaluation.emptyAnswer": "Empty answer.",
    "player.evaluation.openNoReference": "Open question without a reference answer.",
    "player.evaluation.textNeedsSession": "Grading text answers requires a published course and a signed-in session.",
    "player.evaluation.invalidServerScore": "Invalid server response for text grading.",
    "player.evaluation.submitError": "Error while sending.",
    "player.evaluation.passReached": "Pass mark reached.",
    "player.evaluation.passNotReached": "Pass mark not reached.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} pts ({{threshold}}% required)",
    "player.evaluation.questionNumber": "Question {{num}}",
    "player.evaluation.question": "Question",
    "player.evaluation.untitled": "Untitled",
    "player.evaluation.toReview": "To be graded",
    "player.evaluation.points": "{{earned}} / {{max}} pts",
    "player.evaluation.semanticScore": "Semantic score: {{score}}%",
    "player.evaluation.thanks": "Thank you!",
    "player.evaluation.answersSaved": "Your answers have been saved.",
    "player.evaluation.questionCount": "{{count}} question(s)",
    "player.evaluation.start": "Start",
    "player.evaluation.questionProgress": "Question {{num}} / {{total}}",
    "player.evaluation.required": "Required",
    "player.evaluation.chooseAnswer": "Choose an answer…",
    "player.evaluation.answerPlaceholder": "Type your answer…",
    "player.evaluation.chooseFile": "Choose a file",
    "player.evaluation.replaceFile": "Replace the file",
    "player.evaluation.fileHint": "PDF, image, document, text, audio or video — {{max}} MB maximum.",
    "player.evaluation.fileTooLarge": "File too large ({{max}} MB maximum).",
    "player.evaluation.fileBadType": "File type not accepted.",
    "player.evaluation.fileNeedsSession": "Uploading a file requires a published course and a signed-in session.",
    "player.evaluation.fileUploadError": "The file could not be uploaded. Please try again.",
    "player.evaluation.fileUploading": "Uploading {{name}}… {{progress}}%",
    "player.evaluation.fileSent": "File sent: {{name}}",
    "player.evaluation.removeFile": "Remove the file",
    "player.evaluation.fileStillUploading": "Please wait: a file is still uploading.",
    "player.evaluation.fileToReview": "File submitted: it will be graded by the teacher.",
    "player.evaluation.back": "Back",
    "player.evaluation.grading": "Grading…",
    "player.evaluation.finish": "Finish",
    "player.evaluation.next": "Next",
    "player.evaluation.noQuestions": "No question is configured for this evaluation.",
    "player.evaluation.continue": "Continue",
    "player.evaluation.pendingReview": "Provisional result: some answers will be graded by the teacher.",
    "player.evaluation.textToReview": "Answer submitted: it will be graded by the teacher.",
    "share.audience.loading": "Loading…",
    "share.audience.serverError": "Server error ({{status}})",
    "share.audience.networkError": "Network error",
    "share.audience.share": "Share",
    "share.audience.unshare": "Stop sharing",
    "share.audience.noResult": "No results.",
    "share.audience.allShared": "Everything is already shared.",
    "share.audience.members": "{{count}} member(s)",
    "share.audience.members_one": "{{count}} member",
    "share.audience.members_other": "{{count}} members",
    "share.audience.classShared": "{{count}} class(es) shared",
    "share.audience.groupShared": "{{count}} group(s) shared",
    "share.audience.serviceShared": "{{count}} department(s) shared",
    "share.audience.titleClass": "Share with a class",
    "share.audience.titleService": "Share with a department",
    "share.audience.titleGroup": "Share with a group",
    "share.audience.noClassShared": "No class shared.",
    "share.audience.noServiceShared": "No department shared.",
    "share.audience.noGroupShared": "No group shared.",
    "share.audience.searchClass": "Search a class…",
    "share.audience.searchService": "Search a department…",
    "share.audience.searchGroup": "Search a group…",
    "share.audience.emptyClass": "No class. Create classes in the institution dashboard.",
    "share.audience.emptyService": "No department. Create departments in the institution dashboard.",
    "share.audience.emptyGroup": "No group found. Create groups in the institution dashboard.",
    "share.audience.assignedClass": "{{count}} student(s) added out of {{total}} member(s) of the class “{{name}}”.",
    "share.audience.assignedService": "{{count}} learner(s) added out of {{total}} member(s) of the department “{{name}}”.",
    "share.audience.assignedGroup": "{{count}} learner(s) added out of {{total}} member(s) of the group “{{name}}”.",
    "share.audience.shareError": "Sharing failed.",
    "share.audience.unshareClassError": "Could not stop sharing with the class.",
    "share.audience.unshareGroupError": "Could not stop sharing with the group.",
    "profile.page.eyebrow": "Personal space",
    "profile.page.title": "My profile",
    "profile.page.subtitle": "Manage your information, security and preferences in one place.",
    "profile.page.accountStatus": "Account status",
    "profile.page.active": "Active",
    "profile.page.loading": "Loading profile…",
    "profile.page.loadError": "Could not load the profile",
    "profile.page.position": "Job title",
    "profile.page.position_education": "Role",
    "profile.page.service": "Department",
    "profile.page.department": "Department",
    "profile.page.location": "Location",
    "profile.page.tabProfile": "Personal information",
    "profile.page.tabSecurity": "Account security",
    "profile.page.tabPreferences": "Notifications & language",
    "profile.page.profileHint": "Update your contact and job details.",
    "profile.page.profileHint_education": "Update your contact details.",
    "profile.page.firstName": "First name",
    "profile.page.lastName": "Last name",
    "profile.page.email": "Email",
    "profile.page.phone": "Phone",
    "profile.page.city": "Site / City",
    "profile.page.city_education": "City",
    "profile.page.country": "Country",
    "profile.page.saving": "Saving…",
    "profile.page.save": "Save",
    "profile.page.saved": "Profile updated",
    "profile.page.saveError": "Could not update the profile",
    "profile.page.securityHint": "Choose a strong, unique password.",
    "profile.page.passwordMismatch": "The password confirmation does not match",
    "profile.page.passwordChanged": "Password changed",
    "profile.page.passwordError": "Could not change the password",
    "profile.page.preferencesSaved": "Preferences saved",
    "profile.page.preferencesError": "Could not save the preferences",
    "institution.title": "Institution",
    "institution.designers.assignTeam": "Manager’s team",
    "institution.designers.assignTeamEmpty": "No trainer or training lead to assign.",
    "institution.designers.assignTeamHelp": "Tick the trainers and training leads attached to this manager.",
    "institution.designers.cannotRemoveSelf": "You cannot remove your own account here.",
    "institution.designers.colClasses": "Classes",
    "institution.designers.deleteTitle": "Remove this staff member?",
    "institution.designers.groupAdmin": "Administrators",
    "institution.designers.groupEmpty": "No account in this group",
    "institution.designers.groupManager": "Managers",
    "institution.designers.groupRhDrh": "HR / HR director",
    "institution.designers.learnerFormHint": "The sign-in email cannot be changed here. Tick the groups below, then save the record. Department = institution reference list.",
    "institution.designers.learnerFullProfileTitle": "Full learner record",
    "institution.designers.learnerNamesFromHeaderHint": "First and last name come from the top of the record (staff account): they are applied to the learner record when saving.",
    "institution.designers.managerTeam": "Manager’s team",
    "institution.designers.noManager": "No manager",
    "institution.designers.roleDropdownHint": "Drop-down list: click to show all roles.",
    "institution.designers.saveFail": "Could not save",
    "institution.designers.saveTeam": "Save the team",
    "institution.designers.sheetEmailNote": "The sign-in email address cannot be changed here.",
    "institution.designers.sheetTitle": "Staff record",
    "institution.designers.teamNone": "Nobody assigned. Tick trainers or training leads below.",
    "institution.learners.companyArrivalDate": "Company start date",
    "institution.learners.groupsPickHint": "Tick one or more groups, then save. The list comes from the “Groups” tab.",
    "institution.learners.groupsSaveError": "Could not save the groups.",
    "institution.learners.groupsSaved": "Groups saved.",
    "institution.learners.infpc.ageFromBirthHint": "Computed automatically from the date of birth.",
    "institution.learners.infpc.invalidNumber": "Invalid number (seniority)",
    "institution.learners.infpc.isSalariePlus": "Salarié+",
    "institution.learners.lastActive": "Last activity",
    "institution.learners.saveGroups": "Save groups",
    "institution.learners.importResult_one": "{{count}} account created.",
    "institution.learners.importResult_other": "{{count}} accounts created.",
    "player.evaluation.questionCount_one": "{{count}} question",
    "player.evaluation.questionCount_other": "{{count}} questions",
    "share.audience.classShared_one": "{{count}} class shared",
    "share.audience.classShared_other": "{{count}} classes shared",
    "share.audience.groupShared_one": "{{count}} group shared",
    "share.audience.groupShared_other": "{{count}} groups shared",
    "share.audience.serviceShared_one": "{{count}} department shared",
    "share.audience.serviceShared_other": "{{count}} departments shared",
    "institution.menuBlocked.body": "This section has been disabled for your institution by the platform administrator. If you think this is a mistake, contact your administrator.",
    "profile.changeAvatar": "Change profile picture",
    "profile.avatarInvalidType": "Please select an image (JPEG, PNG, WebP, GIF).",
    "profile.avatarUpdated": "Profile picture updated",
    "profile.avatarUploadFailed": "Could not update the profile picture",
    "institution.evaluations.subtitle_education": "Class results on a course. Polls and self-assessment do not count.",
    "institution.evaluations.chooseFlow_education": "Choose a course",
    "institution.evaluations.emptyFlow_education": "No course played by this class yet.",
    "institution.evaluations.emptyLearners_education": "This class has no students yet.",
    "institution.evaluations.learner_education": "Student",
    "institution.evaluations.comment_education": "Comment for the student",
    "institution.classes.flowsTitle_education": "Class courses",
    "institution.classes.flowsHint_education": "Assign a published course to the whole class. Students already enrolled are not duplicated.",
    "institution.classes.chooseFlow_education": "Choose a published course",
    "institution.classes.noFlows_education": "No published course available for this establishment.",
    "institution.classes.subtitle_education": "A class has a head teacher, teachers and students. Import the student list and assign courses to everyone at once.",
    "institution.classes.lead_education": "Head teacher",
    "institution.classes.leadPlaceholder_education": "Choose a head teacher",
    "institution.classes.leadHint_education": "The head teacher follows the class (or a coordinator, a member of the leadership team). Teachers teach their subjects in it.",
    "institution.classes.leadMissing_education": "No head teacher",
    "institution.classes.noLead_education": "First create a teacher, head teacher or administrator account in Teachers.",
    "institution.classes.trainers_education": "Teachers",
    "institution.classes.learners_education": "Students",
    "institution.classes.addTrainer_education": "Add a teacher",
    "institution.classes.addLearner_education": "Add a student",
    "institution.classes.noTrainers_education": "No teacher in this class.",
    "institution.classes.noLearners_education": "No student in this class.",
    "institution.classes.noTrainerAvailable_education": "No teacher available.",
    "institution.classes.noLearnerAvailable_education": "No student available.",
  },
  de: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Aktion verweigert: Ihre Rechte erlauben es nicht, dieses Konto zu verwalten. Wenden Sie sich an eine Administratorin oder einen Administrator der Einrichtung.",
    "institution.designers.readOnlyNotice": "Nur Ansicht: Die Kontoverwaltung ist den Administratoren der Einrichtung vorbehalten.",
    "nav.learners_education": "Schüler",
    "nav.eduflowList_education": "Kurse",
    "institution.tabs.flows_education": "Kurse",
    "institution.tabs.learners_education": "Schüler",
    "institution.tabs.designers_education": "Lehrkräfte",
    "institution.tabs.teachers": "Lehrkräfte",
    "institution.tabs.learnerAnalytics_education": "Schüleranalyse",
    "institution.nav.section.people_education": "Team & Schüler",
    "institution.nav.section.content_education": "Inhalte & Kurse",
    "institution.rhNav.requests_education": "Inhaltsanfragen",
    "rh.requests.title_education": "Inhaltsanfragen",
    "rh.requests.subtitle_education": "Anfragen der Lehrkräfte und Freigaben",
    "header.trainingContentRequest": "Schulungsanfrage (Inhalte)",
    "header.trainingContentRequest_education": "Inhaltsanfrage",
    "dashboard.totalLearners_education": "Schüler gesamt",
    "dashboard.publishedTraining_education": "Veröffentlichte Kurse",
    "dashboard.collaborators": "Mitarbeitende",
    "dashboard.collaborators_education": "Lehrerkollegium",
    "institution.dashboard.registrationCodeLabel": "Registrierungscodes",
    "institution.dashboard.registrationCodeLearner": "Lernende/r",
    "institution.dashboard.registrationCodeLearner_education": "Schüler/in",
    "institution.dashboard.registrationCodeTrainer": "Trainer/in (Lehrkraft)",
    "institution.dashboard.registrationCodeTrainer_education": "Lehrkraft",
    "institution.dashboard.registrationCodeLead": "Schulungsverantwortliche/r (Klassenlehrkraft)",
    "institution.dashboard.registrationCodeLead_education": "Schulleitung",
    "institution.dashboard.registrationCodeCopy": "Kopieren",
    "institution.dashboard.registrationCodeCopied": "Kopiert",
    "institution.dashboard.registrationCodeHelp": "Geben Sie den Code weiter, der zum Kontotyp passt. Ohne ihn kann kein Konto erstellt werden.",
    "institution.flows.title_education": "Kurse",
    "institution.flows.subtitle_education": "Verwalten Sie Ihre Kurse",
    "institution.flows.statTotal_education": "Kurse gesamt",
    "institution.flows.statLearners_education": "Schüler gesamt",
    "institution.flows.searchPh_education": "Kurs suchen…",
    "institution.flows.empty_education": "Kein Kurs gefunden",
    "institution.flows.badgeEnterprise_education": "Inhaltsanfrage",
    "institution.flows.deleteEnterpriseTooltip_education": "In den Papierkorb (Inhaltsanfrage)",
    "institution.flows.deleteEnterpriseTitle_education": "Diesen aus einer Inhaltsanfrage entstandenen Kurs entfernen?",
    "institution.flows.deleteEnterpriseHint_education": "Der Kurs wird in den Papierkorb verschoben (logisches Löschen). Lehrkräfte können das nicht; nur die Schulleitung kann ihn hier wiederherstellen oder entfernen.",
    "institution.flows.deleteEnterpriseSuccess_education": "Kurs in den Papierkorb verschoben.",
    "role.student": "Schüler/in",
    "role.student_education": "Schüler/in",
    "role.learner_education": "Schüler/in",
    "role.teacher_education": "Lehrkraft",
    "institution.designers.roleTeacher_education": "Lehrkraft",
    "institution.designers.roleOptFormateur_education": "Lehrkraft",
    "institution.designers.roleTrainingLead_education": "Klassenlehrkraft",
    "institution.designers.groupTeacher": "Trainer/innen",
    "institution.designers.groupTeacher_education": "Lehrkräfte",
    "institution.designers.groupTrainingLead": "Schulungsverantwortliche",
    "institution.designers.groupTrainingLead_education": "Klassenlehrkräfte",
    "institution.designers.inviteTeacher": "Lehrkraft einladen",
    "institution.designers.inviteTeacherTitle": "Lehrkraft einladen",
    "institution.designers.inviteTeacherHelp": "Lehrkraft: unterrichtet in Klassen. Klassenlehrkraft: betreut eine Klasse.",
    "institution.designers.statTeachers": "Lehrkräfte",
    "institution.designers.teachersSubtitle": "Lehrkräfte und Klassenlehrkräfte der Schule.",
    "institution.learners.title_education": "Schüler",
    "institution.learners.statLearners_education": "Schüler",
    "institution.learners.subtitle_education": "{{count}} Schüler an Ihrer Schule eingeschrieben",
    "institution.learners.edu.courses": "Kurse",
    "institution.learners.edu.coursesDone": "Abgeschlossene Kurse",
    "institution.learners.edu.classTitle": "Klasse",
    "institution.learners.edu.classHint": "Die Klasse wird im Tab „Klassen“ verwaltet.",
    "institution.learners.edu.noClass": "Keine Klasse",
    "institution.learners.edu.profileTitle": "Angaben zum Schüler",
    "institution.learners.edu.profileHint": "Geschlecht, Geburtsdatum und Abschluss des Schülers.",
    "institution.learners.edu.diploma": "Erworbener Abschluss",
    "institution.learners.edu.saveProfile": "Datenblatt speichern",
    "institution.menuBlocked.segmentBody": "Diese Funktion ist für Ihre Art von Einrichtung nicht verfügbar.",
    "pages.learners.sortRecentPage": "Letzte Aktivität (aktuelle Seite)",
    "pages.learners.sortProgressPage": "Fortschritt (aktuelle Seite)",
    "pages.learners.statsPageHint": "In Bearbeitung, Abgeschlossen und Ø Fortschritt: berechnet für die {{count}} Personen der aktuellen Seite.",
    "pages.learners.exporting": "Export läuft…",
    "pages.learners.exportError": "Export fehlgeschlagen. Bitte erneut versuchen.",
    "institution.learners.importMissingFields": "Vorname, Nachname und E-Mail sind Pflichtfelder.",
    "institution.learners.importBadDate": "Unlesbares Datum: TT/MM/JJJJ oder JJJJ-MM-TT verwenden.",
    "institution.learners.importNoRows": "Keine importierbare Zeile gefunden.",
    "institution.learners.importResult": "{{count}} Konto/Konten erstellt.",
    "institution.learners.downloadTemplate": "CSV-Vorlage",
    "institution.classes.noAssignedFlows": "Dieser Klasse ist noch kein Kurs zugewiesen. Wählen Sie oben einen aus.",
    "institution.evaluations.pointsRequired": "Geben Sie vor dem Bestätigen eine Note ein.",
    "institution.evaluations.pointsRange": "Die Note muss zwischen 0 und {{max}} liegen.",
    "institution.evaluations.pointsInvalid": "Ungültige Note.",
    "institution.evaluations.points": "Note",
    "institution.evaluations.saveError": "Die Note konnte nicht gespeichert werden.",
    "institution.evaluations.settingsSaveError": "Die Regeln konnten nicht gespeichert werden.",
    "institution.evaluations.applyLevelError": "Die Stufe konnte nicht aktualisiert werden.",
    "institution.evaluations.downloadFile": "Datei herunterladen",
    "institution.evaluations.fileDownloadError": "Die Datei konnte nicht heruntergeladen werden.",
    "institution.learners.importDateIgnored": "{{field}} unlesbar, ignoriert (TT/MM/JJJJ oder JJJJ-MM-TT).",
    "institution.learners.importFieldBirthDate": "Geburtsdatum",
    "institution.learners.importFieldHireDate": "Einstellungsdatum",
    "institution.learners.importFieldArrivalDate": "Eintrittsdatum",
    "institution.learners.importWarnings": "{{count}} Warnung(en) (Zeile importiert):",
    "player.evaluation.type.text": "Freitext",
    "player.evaluation.type.singleChoice": "Einfachauswahl",
    "player.evaluation.type.multipleChoice": "Mehrfachauswahl",
    "player.evaluation.type.dropdown": "Auswahlliste",
    "player.evaluation.type.numeric": "Numerisch",
    "player.evaluation.type.fileUpload": "Datei",
    "player.evaluation.requiredAll": "Bitte beantworten Sie alle Pflichtfragen.",
    "player.evaluation.requiredOne": "Bitte beantworten Sie diese Frage, um fortzufahren.",
    "player.evaluation.emptyAnswer": "Leere Antwort.",
    "player.evaluation.openNoReference": "Offene Frage ohne Referenzantwort.",
    "player.evaluation.textNeedsSession": "Die Bewertung von Textantworten erfordert einen veröffentlichten Kurs und eine angemeldete Sitzung.",
    "player.evaluation.invalidServerScore": "Ungültige Serverantwort bei der Textbewertung.",
    "player.evaluation.submitError": "Fehler beim Senden.",
    "player.evaluation.passReached": "Bestehensgrenze erreicht.",
    "player.evaluation.passNotReached": "Bestehensgrenze nicht erreicht.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} Pkt. ({{threshold}} % erforderlich)",
    "player.evaluation.questionNumber": "Frage {{num}}",
    "player.evaluation.question": "Frage",
    "player.evaluation.untitled": "Ohne Titel",
    "player.evaluation.toReview": "Zu korrigieren",
    "player.evaluation.points": "{{earned}} / {{max}} Pkt.",
    "player.evaluation.semanticScore": "Semantischer Wert: {{score}} %",
    "player.evaluation.thanks": "Danke!",
    "player.evaluation.answersSaved": "Ihre Antworten wurden gespeichert.",
    "player.evaluation.questionCount": "{{count}} Frage(n)",
    "player.evaluation.start": "Starten",
    "player.evaluation.questionProgress": "Frage {{num}} / {{total}}",
    "player.evaluation.required": "Pflichtfeld",
    "player.evaluation.chooseAnswer": "Antwort wählen…",
    "player.evaluation.answerPlaceholder": "Geben Sie Ihre Antwort ein…",
    "player.evaluation.chooseFile": "Datei auswählen",
    "player.evaluation.replaceFile": "Datei ersetzen",
    "player.evaluation.fileHint": "PDF, Bild, Dokument, Text, Audio oder Video — maximal {{max}} MB.",
    "player.evaluation.fileTooLarge": "Datei zu groß (maximal {{max}} MB).",
    "player.evaluation.fileBadType": "Dateityp nicht zulässig.",
    "player.evaluation.fileNeedsSession": "Das Hochladen einer Datei erfordert einen veröffentlichten Kurs und eine angemeldete Sitzung.",
    "player.evaluation.fileUploadError": "Die Datei konnte nicht hochgeladen werden. Bitte erneut versuchen.",
    "player.evaluation.fileUploading": "{{name}} wird hochgeladen… {{progress}} %",
    "player.evaluation.fileSent": "Datei gesendet: {{name}}",
    "player.evaluation.removeFile": "Datei entfernen",
    "player.evaluation.fileStillUploading": "Bitte warten: Eine Datei wird noch hochgeladen.",
    "player.evaluation.fileToReview": "Datei übermittelt: Sie wird von der Lehrkraft bewertet.",
    "player.evaluation.back": "Zurück",
    "player.evaluation.grading": "Bewertung…",
    "player.evaluation.finish": "Abschließen",
    "player.evaluation.next": "Weiter",
    "player.evaluation.noQuestions": "Für diese Bewertung ist keine Frage eingerichtet.",
    "player.evaluation.continue": "Weiter",
    "player.evaluation.pendingReview": "Vorläufiges Ergebnis: Einige Antworten werden von der Lehrkraft bewertet.",
    "player.evaluation.textToReview": "Antwort übermittelt: Sie wird von der Lehrkraft bewertet.",
    "share.audience.loading": "Wird geladen…",
    "share.audience.serverError": "Serverfehler ({{status}})",
    "share.audience.networkError": "Netzwerkfehler",
    "share.audience.share": "Teilen",
    "share.audience.unshare": "Freigabe entfernen",
    "share.audience.noResult": "Keine Ergebnisse.",
    "share.audience.allShared": "Alles ist bereits geteilt.",
    "share.audience.members": "{{count}} Mitglied(er)",
    "share.audience.members_one": "{{count}} Mitglied",
    "share.audience.members_other": "{{count}} Mitglieder",
    "share.audience.classShared": "{{count}} Klasse(n) geteilt",
    "share.audience.groupShared": "{{count}} Gruppe(n) geteilt",
    "share.audience.serviceShared": "{{count}} Abteilung(en) geteilt",
    "share.audience.titleClass": "Mit einer Klasse teilen",
    "share.audience.titleService": "Mit einer Abteilung teilen",
    "share.audience.titleGroup": "Mit einer Gruppe teilen",
    "share.audience.noClassShared": "Keine Klasse geteilt.",
    "share.audience.noServiceShared": "Keine Abteilung geteilt.",
    "share.audience.noGroupShared": "Keine Gruppe geteilt.",
    "share.audience.searchClass": "Klasse suchen…",
    "share.audience.searchService": "Abteilung suchen…",
    "share.audience.searchGroup": "Gruppe suchen…",
    "share.audience.emptyClass": "Keine Klasse. Legen Sie Klassen im Einrichtungs-Dashboard an.",
    "share.audience.emptyService": "Keine Abteilung. Legen Sie Abteilungen im Einrichtungs-Dashboard an.",
    "share.audience.emptyGroup": "Keine Gruppe gefunden. Legen Sie Gruppen im Einrichtungs-Dashboard an.",
    "share.audience.assignedClass": "{{count}} Schüler von {{total}} Mitglied(ern) der Klasse „{{name}}“ hinzugefügt.",
    "share.audience.assignedService": "{{count}} Lernende von {{total}} Mitglied(ern) der Abteilung „{{name}}“ hinzugefügt.",
    "share.audience.assignedGroup": "{{count}} Lernende von {{total}} Mitglied(ern) der Gruppe „{{name}}“ hinzugefügt.",
    "share.audience.shareError": "Teilen fehlgeschlagen.",
    "share.audience.unshareClassError": "Die Freigabe für die Klasse konnte nicht entfernt werden.",
    "share.audience.unshareGroupError": "Die Freigabe für die Gruppe konnte nicht entfernt werden.",
    "profile.page.eyebrow": "Persönlicher Bereich",
    "profile.page.title": "Mein Profil",
    "profile.page.subtitle": "Verwalten Sie Ihre Daten, Sicherheit und Einstellungen an einem Ort.",
    "profile.page.accountStatus": "Kontostatus",
    "profile.page.active": "Aktiv",
    "profile.page.loading": "Profil wird geladen…",
    "profile.page.loadError": "Profil konnte nicht geladen werden",
    "profile.page.position": "Position",
    "profile.page.position_education": "Funktion",
    "profile.page.service": "Abteilung",
    "profile.page.department": "Abteilung",
    "profile.page.location": "Standort",
    "profile.page.tabProfile": "Persönliche Angaben",
    "profile.page.tabSecurity": "Kontosicherheit",
    "profile.page.tabPreferences": "Benachrichtigungen & Sprache",
    "profile.page.profileHint": "Aktualisieren Sie Ihre Kontakt- und Stellenangaben.",
    "profile.page.profileHint_education": "Aktualisieren Sie Ihre Kontaktangaben.",
    "profile.page.firstName": "Vorname",
    "profile.page.lastName": "Nachname",
    "profile.page.email": "E-Mail",
    "profile.page.phone": "Telefon",
    "profile.page.city": "Standort / Stadt",
    "profile.page.city_education": "Stadt",
    "profile.page.country": "Land",
    "profile.page.saving": "Wird gespeichert…",
    "profile.page.save": "Speichern",
    "profile.page.saved": "Profil aktualisiert",
    "profile.page.saveError": "Profil konnte nicht aktualisiert werden",
    "profile.page.securityHint": "Wählen Sie ein starkes, einmaliges Passwort.",
    "profile.page.passwordMismatch": "Die Passwortbestätigung stimmt nicht überein",
    "profile.page.passwordChanged": "Passwort geändert",
    "profile.page.passwordError": "Passwort konnte nicht geändert werden",
    "profile.page.preferencesSaved": "Einstellungen gespeichert",
    "profile.page.preferencesError": "Einstellungen konnten nicht gespeichert werden",
  },
  es: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Acción denegada: sus permisos no le permiten gestionar esta cuenta. Póngase en contacto con un administrador del centro.",
    "institution.designers.readOnlyNotice": "Solo lectura: la gestión de cuentas está reservada a los administradores del centro.",
    "nav.learners_education": "Alumnos",
    "nav.eduflowList_education": "Cursos",
    "institution.tabs.flows_education": "Cursos",
    "institution.tabs.learners_education": "Alumnos",
    "institution.tabs.designers_education": "Profesores",
    "institution.tabs.teachers": "Profesores",
    "institution.tabs.learnerAnalytics_education": "Análisis de alumnos",
    "institution.nav.section.people_education": "Equipo y alumnos",
    "institution.nav.section.content_education": "Contenidos y cursos",
    "institution.rhNav.requests_education": "Solicitudes de contenido",
    "rh.requests.title_education": "Solicitudes de contenido",
    "rh.requests.subtitle_education": "Solicitudes de los profesores y validaciones",
    "header.trainingContentRequest": "Solicitud de formación (contenidos)",
    "header.trainingContentRequest_education": "Solicitud de contenido",
    "dashboard.totalLearners_education": "Total de alumnos",
    "dashboard.publishedTraining_education": "Cursos publicados",
    "dashboard.collaborators": "Colaboradores",
    "dashboard.collaborators_education": "Equipo docente",
    "institution.dashboard.registrationCodeLabel": "Códigos de inscripción",
    "institution.dashboard.registrationCodeLearner": "Aprendiz",
    "institution.dashboard.registrationCodeLearner_education": "Alumno",
    "institution.dashboard.registrationCodeTrainer": "Formador (profesor)",
    "institution.dashboard.registrationCodeTrainer_education": "Profesor",
    "institution.dashboard.registrationCodeLead": "Responsable de formación (tutor)",
    "institution.dashboard.registrationCodeLead_education": "Dirección",
    "institution.dashboard.registrationCodeCopy": "Copiar",
    "institution.dashboard.registrationCodeCopied": "Copiado",
    "institution.dashboard.registrationCodeHelp": "Comunique el código que corresponde al tipo de cuenta. No se puede crear una cuenta sin él.",
    "institution.flows.title_education": "Cursos",
    "institution.flows.subtitle_education": "Gestione sus cursos",
    "institution.flows.statTotal_education": "Total de cursos",
    "institution.flows.statLearners_education": "Total de alumnos",
    "institution.flows.searchPh_education": "Buscar un curso…",
    "institution.flows.empty_education": "No se encontró ningún curso",
    "institution.flows.badgeEnterprise_education": "Solicitud de contenido",
    "institution.flows.deleteEnterpriseTooltip_education": "Mover a la papelera (solicitud de contenido)",
    "institution.flows.deleteEnterpriseTitle_education": "¿Retirar este curso creado a partir de una solicitud de contenido?",
    "institution.flows.deleteEnterpriseHint_education": "El curso se moverá a la papelera (borrado lógico). Los profesores no pueden hacerlo; solo la dirección del centro puede restaurarlo o retirarlo desde este panel.",
    "institution.flows.deleteEnterpriseSuccess_education": "Curso movido a la papelera.",
    "role.student": "Alumno",
    "role.student_education": "Alumno",
    "role.learner_education": "Alumno",
    "role.teacher_education": "Profesor",
    "institution.designers.roleTeacher_education": "Profesor",
    "institution.designers.roleOptFormateur_education": "Profesor",
    "institution.designers.roleTrainingLead_education": "Tutor",
    "institution.designers.groupTeacher": "Formadores",
    "institution.designers.groupTeacher_education": "Profesores",
    "institution.designers.groupTrainingLead": "Responsables de formación",
    "institution.designers.groupTrainingLead_education": "Tutores",
    "institution.designers.inviteTeacher": "Invitar a un profesor",
    "institution.designers.inviteTeacherTitle": "Invitar a un profesor",
    "institution.designers.inviteTeacherHelp": "Profesor: imparte clase en los grupos. Tutor: hace el seguimiento de una clase.",
    "institution.designers.statTeachers": "Profesores",
    "institution.designers.teachersSubtitle": "Profesores y tutores del centro.",
    "institution.learners.title_education": "Alumnos",
    "institution.learners.statLearners_education": "Alumnos",
    "institution.learners.subtitle_education": "{{count}} alumno(s) inscrito(s) en su centro",
    "institution.learners.edu.courses": "Cursos",
    "institution.learners.edu.coursesDone": "Cursos terminados",
    "institution.learners.edu.classTitle": "Clase",
    "institution.learners.edu.classHint": "La clase se gestiona en la pestaña Clases.",
    "institution.learners.edu.noClass": "Sin clase",
    "institution.learners.edu.profileTitle": "Información del alumno",
    "institution.learners.edu.profileHint": "Género, fecha de nacimiento y título del alumno.",
    "institution.learners.edu.diploma": "Título obtenido",
    "institution.learners.edu.saveProfile": "Guardar la ficha",
    "institution.menuBlocked.segmentBody": "Esta función no está disponible para su tipo de centro.",
    "pages.learners.sortRecentPage": "Última actividad (página actual)",
    "pages.learners.sortProgressPage": "Progreso (página actual)",
    "pages.learners.statsPageHint": "En curso, Terminado y Progreso medio: calculados sobre las {{count}} personas de la página actual.",
    "pages.learners.exporting": "Exportando…",
    "pages.learners.exportError": "No se pudo exportar. Inténtelo de nuevo.",
    "institution.learners.importMissingFields": "El nombre, el apellido y el correo son obligatorios.",
    "institution.learners.importBadDate": "Fecha ilegible: use DD/MM/AAAA o AAAA-MM-DD.",
    "institution.learners.importNoRows": "No se encontró ninguna fila importable.",
    "institution.learners.importResult": "{{count}} cuenta(s) creada(s).",
    "institution.learners.downloadTemplate": "Plantilla CSV",
    "institution.classes.noAssignedFlows": "Todavía no hay ningún curso asignado a esta clase. Elija uno arriba.",
    "institution.evaluations.pointsRequired": "Introduzca una nota antes de validar.",
    "institution.evaluations.pointsRange": "La nota debe estar entre 0 y {{max}}.",
    "institution.evaluations.pointsInvalid": "Nota no válida.",
    "institution.evaluations.points": "Nota",
    "institution.evaluations.saveError": "No se pudo guardar la nota.",
    "institution.evaluations.settingsSaveError": "No se pudieron guardar las reglas.",
    "institution.evaluations.applyLevelError": "No se pudo actualizar el nivel.",
    "institution.evaluations.downloadFile": "Descargar el archivo",
    "institution.evaluations.fileDownloadError": "No se pudo descargar el archivo.",
    "institution.learners.importDateIgnored": "{{field}} ilegible, ignorada (DD/MM/AAAA o AAAA-MM-DD).",
    "institution.learners.importFieldBirthDate": "Fecha de nacimiento",
    "institution.learners.importFieldHireDate": "Fecha de contratación",
    "institution.learners.importFieldArrivalDate": "Fecha de incorporación",
    "institution.learners.importWarnings": "{{count}} aviso(s) (fila importada):",
    "player.evaluation.type.text": "Texto libre",
    "player.evaluation.type.singleChoice": "Opción única",
    "player.evaluation.type.multipleChoice": "Opción múltiple",
    "player.evaluation.type.dropdown": "Lista desplegable",
    "player.evaluation.type.numeric": "Numérica",
    "player.evaluation.type.fileUpload": "Archivo",
    "player.evaluation.requiredAll": "Responda a todas las preguntas obligatorias.",
    "player.evaluation.requiredOne": "Responda a esta pregunta para continuar.",
    "player.evaluation.emptyAnswer": "Respuesta vacía.",
    "player.evaluation.openNoReference": "Pregunta abierta sin respuesta de referencia.",
    "player.evaluation.textNeedsSession": "La corrección de respuestas de texto requiere un curso publicado y una sesión iniciada.",
    "player.evaluation.invalidServerScore": "Respuesta del servidor no válida para la calificación de texto.",
    "player.evaluation.submitError": "Error al enviar.",
    "player.evaluation.passReached": "Umbral de aprobado alcanzado.",
    "player.evaluation.passNotReached": "Umbral de aprobado no alcanzado.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} pts ({{threshold}} % requerido)",
    "player.evaluation.questionNumber": "Pregunta {{num}}",
    "player.evaluation.question": "Pregunta",
    "player.evaluation.untitled": "Sin título",
    "player.evaluation.toReview": "Por corregir",
    "player.evaluation.points": "{{earned}} / {{max}} pts",
    "player.evaluation.semanticScore": "Puntuación semántica: {{score}} %",
    "player.evaluation.thanks": "¡Gracias!",
    "player.evaluation.answersSaved": "Sus respuestas se han guardado.",
    "player.evaluation.questionCount": "{{count}} pregunta(s)",
    "player.evaluation.start": "Empezar",
    "player.evaluation.questionProgress": "Pregunta {{num}} / {{total}}",
    "player.evaluation.required": "Obligatoria",
    "player.evaluation.chooseAnswer": "Elija una respuesta…",
    "player.evaluation.answerPlaceholder": "Escriba su respuesta…",
    "player.evaluation.chooseFile": "Elegir un archivo",
    "player.evaluation.replaceFile": "Reemplazar el archivo",
    "player.evaluation.fileHint": "PDF, imagen, documento, texto, audio o vídeo — máximo {{max}} MB.",
    "player.evaluation.fileTooLarge": "Archivo demasiado grande (máximo {{max}} MB).",
    "player.evaluation.fileBadType": "Tipo de archivo no admitido.",
    "player.evaluation.fileNeedsSession": "Subir un archivo requiere un curso publicado y una sesión iniciada.",
    "player.evaluation.fileUploadError": "No se pudo subir el archivo. Inténtelo de nuevo.",
    "player.evaluation.fileUploading": "Subiendo {{name}}… {{progress}} %",
    "player.evaluation.fileSent": "Archivo enviado: {{name}}",
    "player.evaluation.removeFile": "Quitar el archivo",
    "player.evaluation.fileStillUploading": "Espere: todavía se está subiendo un archivo.",
    "player.evaluation.fileToReview": "Archivo enviado: lo corregirá el profesor.",
    "player.evaluation.back": "Atrás",
    "player.evaluation.grading": "Corrigiendo…",
    "player.evaluation.finish": "Terminar",
    "player.evaluation.next": "Siguiente",
    "player.evaluation.noQuestions": "No hay ninguna pregunta configurada para esta evaluación.",
    "player.evaluation.continue": "Continuar",
    "player.evaluation.pendingReview": "Resultado provisional: el profesor corregirá algunas respuestas.",
    "player.evaluation.textToReview": "Respuesta enviada: la corregirá el profesor.",
    "share.audience.loading": "Cargando…",
    "share.audience.serverError": "Error del servidor ({{status}})",
    "share.audience.networkError": "Error de red",
    "share.audience.share": "Compartir",
    "share.audience.unshare": "Dejar de compartir",
    "share.audience.noResult": "Sin resultados.",
    "share.audience.allShared": "Todo está ya compartido.",
    "share.audience.members": "{{count}} miembro(s)",
    "share.audience.members_one": "{{count}} miembro",
    "share.audience.members_other": "{{count}} miembros",
    "share.audience.classShared": "{{count}} clase(s) compartida(s)",
    "share.audience.groupShared": "{{count}} grupo(s) compartido(s)",
    "share.audience.serviceShared": "{{count}} servicio(s) compartido(s)",
    "share.audience.titleClass": "Compartir con una clase",
    "share.audience.titleService": "Compartir con un servicio",
    "share.audience.titleGroup": "Compartir con un grupo",
    "share.audience.noClassShared": "Ninguna clase compartida.",
    "share.audience.noServiceShared": "Ningún servicio compartido.",
    "share.audience.noGroupShared": "Ningún grupo compartido.",
    "share.audience.searchClass": "Buscar una clase…",
    "share.audience.searchService": "Buscar un servicio…",
    "share.audience.searchGroup": "Buscar un grupo…",
    "share.audience.emptyClass": "Ninguna clase. Cree clases en el panel del centro.",
    "share.audience.emptyService": "Ningún servicio. Cree servicios en el panel del centro.",
    "share.audience.emptyGroup": "No se encontró ningún grupo. Cree grupos en el panel del centro.",
    "share.audience.assignedClass": "{{count}} alumno(s) añadido(s) de {{total}} miembro(s) de la clase «{{name}}».",
    "share.audience.assignedService": "{{count}} aprendiz(ces) añadido(s) de {{total}} miembro(s) del servicio «{{name}}».",
    "share.audience.assignedGroup": "{{count}} aprendiz(ces) añadido(s) de {{total}} miembro(s) del grupo «{{name}}».",
    "share.audience.shareError": "No se pudo compartir.",
    "share.audience.unshareClassError": "No se pudo dejar de compartir con la clase.",
    "share.audience.unshareGroupError": "No se pudo dejar de compartir con el grupo.",
    "profile.page.eyebrow": "Espacio personal",
    "profile.page.title": "Mi perfil",
    "profile.page.subtitle": "Centralice su información, seguridad y preferencias.",
    "profile.page.accountStatus": "Estado de la cuenta",
    "profile.page.active": "Activa",
    "profile.page.loading": "Cargando el perfil…",
    "profile.page.loadError": "No se pudo cargar el perfil",
    "profile.page.position": "Puesto",
    "profile.page.position_education": "Función",
    "profile.page.service": "Servicio",
    "profile.page.department": "Departamento",
    "profile.page.location": "Ubicación",
    "profile.page.tabProfile": "Información personal",
    "profile.page.tabSecurity": "Seguridad de la cuenta",
    "profile.page.tabPreferences": "Notificaciones e idioma",
    "profile.page.profileHint": "Actualice sus datos de contacto y de puesto.",
    "profile.page.profileHint_education": "Actualice sus datos de contacto.",
    "profile.page.firstName": "Nombre",
    "profile.page.lastName": "Apellido",
    "profile.page.email": "Correo electrónico",
    "profile.page.phone": "Teléfono",
    "profile.page.city": "Sede / Ciudad",
    "profile.page.city_education": "Ciudad",
    "profile.page.country": "País",
    "profile.page.saving": "Guardando…",
    "profile.page.save": "Guardar",
    "profile.page.saved": "Perfil actualizado",
    "profile.page.saveError": "No se pudo actualizar el perfil",
    "profile.page.securityHint": "Elija una contraseña segura y única.",
    "profile.page.passwordMismatch": "La confirmación de la contraseña no coincide",
    "profile.page.passwordChanged": "Contraseña modificada",
    "profile.page.passwordError": "No se pudo cambiar la contraseña",
    "profile.page.preferencesSaved": "Preferencias guardadas",
    "profile.page.preferencesError": "No se pudieron guardar las preferencias",
  },
  it: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Azione negata: i tuoi permessi non consentono di gestire questo account. Contatta un amministratore dell’istituto.",
    "institution.designers.readOnlyNotice": "Sola consultazione: la gestione degli account è riservata agli amministratori dell’istituto.",
    "nav.learners_education": "Studenti",
    "nav.eduflowList_education": "Corsi",
    "institution.tabs.flows_education": "Corsi",
    "institution.tabs.learners_education": "Studenti",
    "institution.tabs.designers_education": "Docenti",
    "institution.tabs.teachers": "Docenti",
    "institution.tabs.learnerAnalytics_education": "Analisi studenti",
    "institution.nav.section.people_education": "Personale e studenti",
    "institution.nav.section.content_education": "Contenuti e corsi",
    "institution.rhNav.requests_education": "Richieste di contenuti",
    "rh.requests.title_education": "Richieste di contenuti",
    "rh.requests.subtitle_education": "Richieste dei docenti e approvazioni",
    "header.trainingContentRequest": "Richiesta di formazione (contenuti)",
    "header.trainingContentRequest_education": "Richiesta di contenuto",
    "dashboard.totalLearners_education": "Totale studenti",
    "dashboard.publishedTraining_education": "Corsi pubblicati",
    "dashboard.collaborators": "Collaboratori",
    "dashboard.collaborators_education": "Corpo docente",
    "institution.dashboard.registrationCodeLabel": "Codici di iscrizione",
    "institution.dashboard.registrationCodeLearner": "Discente",
    "institution.dashboard.registrationCodeLearner_education": "Studente",
    "institution.dashboard.registrationCodeTrainer": "Formatore (docente)",
    "institution.dashboard.registrationCodeTrainer_education": "Docente",
    "institution.dashboard.registrationCodeLead": "Responsabile formazione (coordinatore di classe)",
    "institution.dashboard.registrationCodeLead_education": "Dirigenza",
    "institution.dashboard.registrationCodeCopy": "Copia",
    "institution.dashboard.registrationCodeCopied": "Copiato",
    "institution.dashboard.registrationCodeHelp": "Comunicate il codice corrispondente al tipo di account. Senza di esso non è possibile creare un account.",
    "institution.flows.title_education": "Corsi",
    "institution.flows.subtitle_education": "Gestite i vostri corsi",
    "institution.flows.statTotal_education": "Totale corsi",
    "institution.flows.statLearners_education": "Totale studenti",
    "institution.flows.searchPh_education": "Cerca un corso…",
    "institution.flows.empty_education": "Nessun corso trovato",
    "institution.flows.badgeEnterprise_education": "Richiesta di contenuto",
    "institution.flows.deleteEnterpriseTooltip_education": "Sposta nel cestino (richiesta di contenuto)",
    "institution.flows.deleteEnterpriseTitle_education": "Rimuovere questo corso nato da una richiesta di contenuto?",
    "institution.flows.deleteEnterpriseHint_education": "Il corso sarà spostato nel cestino (eliminazione logica). I docenti non possono farlo; solo la dirigenza può ripristinarlo o rimuoverlo da questo pannello.",
    "institution.flows.deleteEnterpriseSuccess_education": "Corso spostato nel cestino.",
    "role.student": "Studente",
    "role.student_education": "Studente",
    "role.learner_education": "Studente",
    "role.teacher_education": "Docente",
    "institution.designers.roleTeacher_education": "Docente",
    "institution.designers.roleOptFormateur_education": "Docente",
    "institution.designers.roleTrainingLead_education": "Coordinatore di classe",
    "institution.designers.groupTeacher": "Formatori",
    "institution.designers.groupTeacher_education": "Docenti",
    "institution.designers.groupTrainingLead": "Responsabili formazione",
    "institution.designers.groupTrainingLead_education": "Coordinatori di classe",
    "institution.designers.inviteTeacher": "Invita un docente",
    "institution.designers.inviteTeacherTitle": "Invita un docente",
    "institution.designers.inviteTeacherHelp": "Docente: insegna nelle classi. Coordinatore di classe: segue una classe.",
    "institution.designers.statTeachers": "Docenti",
    "institution.designers.teachersSubtitle": "Docenti e coordinatori di classe dell’istituto.",
    "institution.learners.title_education": "Studenti",
    "institution.learners.statLearners_education": "Studenti",
    "institution.learners.subtitle_education": "{{count}} studente/i iscritto/i nel vostro istituto",
    "institution.learners.edu.courses": "Corsi",
    "institution.learners.edu.coursesDone": "Corsi completati",
    "institution.learners.edu.classTitle": "Classe",
    "institution.learners.edu.classHint": "La classe si gestisce nella scheda Classi.",
    "institution.learners.edu.noClass": "Nessuna classe",
    "institution.learners.edu.profileTitle": "Informazioni sullo studente",
    "institution.learners.edu.profileHint": "Genere, data di nascita e titolo di studio dello studente.",
    "institution.learners.edu.diploma": "Titolo conseguito",
    "institution.learners.edu.saveProfile": "Salva la scheda",
    "institution.menuBlocked.segmentBody": "Questa funzione non è disponibile per il vostro tipo di istituto.",
    "pages.learners.sortRecentPage": "Ultima attività (pagina corrente)",
    "pages.learners.sortProgressPage": "Avanzamento (pagina corrente)",
    "pages.learners.statsPageHint": "In corso, Completato e Avanzamento medio: calcolati sulle {{count}} persone della pagina corrente.",
    "pages.learners.exporting": "Esportazione…",
    "pages.learners.exportError": "Esportazione non riuscita. Riprovate.",
    "institution.learners.importMissingFields": "Nome, cognome ed e-mail sono obbligatori.",
    "institution.learners.importBadDate": "Data illeggibile: usate GG/MM/AAAA o AAAA-MM-GG.",
    "institution.learners.importNoRows": "Nessuna riga importabile trovata.",
    "institution.learners.importResult": "{{count}} account creato/i.",
    "institution.learners.downloadTemplate": "Modello CSV",
    "institution.classes.noAssignedFlows": "Nessun corso è ancora assegnato a questa classe. Sceglietene uno qui sopra.",
    "institution.evaluations.pointsRequired": "Inserite un voto prima di convalidare.",
    "institution.evaluations.pointsRange": "Il voto deve essere compreso tra 0 e {{max}}.",
    "institution.evaluations.pointsInvalid": "Voto non valido.",
    "institution.evaluations.points": "Voto",
    "institution.evaluations.saveError": "Impossibile salvare il voto.",
    "institution.evaluations.settingsSaveError": "Impossibile salvare le regole.",
    "institution.evaluations.applyLevelError": "Impossibile aggiornare il livello.",
    "institution.evaluations.downloadFile": "Scarica il file",
    "institution.evaluations.fileDownloadError": "Impossibile scaricare il file.",
    "institution.learners.importDateIgnored": "{{field}} illeggibile, ignorata (GG/MM/AAAA o AAAA-MM-GG).",
    "institution.learners.importFieldBirthDate": "Data di nascita",
    "institution.learners.importFieldHireDate": "Data di assunzione",
    "institution.learners.importFieldArrivalDate": "Data di arrivo",
    "institution.learners.importWarnings": "{{count}} avviso/i (riga importata):",
    "player.evaluation.type.text": "Testo libero",
    "player.evaluation.type.singleChoice": "Scelta singola",
    "player.evaluation.type.multipleChoice": "Scelta multipla",
    "player.evaluation.type.dropdown": "Elenco a discesa",
    "player.evaluation.type.numeric": "Numerica",
    "player.evaluation.type.fileUpload": "File",
    "player.evaluation.requiredAll": "Rispondete a tutte le domande obbligatorie.",
    "player.evaluation.requiredOne": "Rispondete a questa domanda per continuare.",
    "player.evaluation.emptyAnswer": "Risposta vuota.",
    "player.evaluation.openNoReference": "Domanda aperta senza risposta di riferimento.",
    "player.evaluation.textNeedsSession": "La correzione delle risposte di testo richiede un corso pubblicato e una sessione connessa.",
    "player.evaluation.invalidServerScore": "Risposta del server non valida per la valutazione del testo.",
    "player.evaluation.submitError": "Errore durante l’invio.",
    "player.evaluation.passReached": "Soglia di superamento raggiunta.",
    "player.evaluation.passNotReached": "Soglia di superamento non raggiunta.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} pt ({{threshold}}% richiesto)",
    "player.evaluation.questionNumber": "Domanda {{num}}",
    "player.evaluation.question": "Domanda",
    "player.evaluation.untitled": "Senza titolo",
    "player.evaluation.toReview": "Da correggere",
    "player.evaluation.points": "{{earned}} / {{max}} pt",
    "player.evaluation.semanticScore": "Punteggio semantico: {{score}}%",
    "player.evaluation.thanks": "Grazie!",
    "player.evaluation.answersSaved": "Le vostre risposte sono state salvate.",
    "player.evaluation.questionCount": "{{count}} domanda/e",
    "player.evaluation.start": "Inizia",
    "player.evaluation.questionProgress": "Domanda {{num}} / {{total}}",
    "player.evaluation.required": "Obbligatoria",
    "player.evaluation.chooseAnswer": "Scegliete una risposta…",
    "player.evaluation.answerPlaceholder": "Scrivete la vostra risposta…",
    "player.evaluation.chooseFile": "Scegli un file",
    "player.evaluation.replaceFile": "Sostituisci il file",
    "player.evaluation.fileHint": "PDF, immagine, documento, testo, audio o video — massimo {{max}} MB.",
    "player.evaluation.fileTooLarge": "File troppo grande (massimo {{max}} MB).",
    "player.evaluation.fileBadType": "Tipo di file non accettato.",
    "player.evaluation.fileNeedsSession": "Il caricamento di un file richiede un corso pubblicato e una sessione connessa.",
    "player.evaluation.fileUploadError": "Impossibile caricare il file. Riprovate.",
    "player.evaluation.fileUploading": "Caricamento di {{name}}… {{progress}}%",
    "player.evaluation.fileSent": "File inviato: {{name}}",
    "player.evaluation.removeFile": "Rimuovi il file",
    "player.evaluation.fileStillUploading": "Attendete: un file è ancora in caricamento.",
    "player.evaluation.fileToReview": "File inviato: sarà corretto dal docente.",
    "player.evaluation.back": "Indietro",
    "player.evaluation.grading": "Correzione…",
    "player.evaluation.finish": "Termina",
    "player.evaluation.next": "Avanti",
    "player.evaluation.noQuestions": "Nessuna domanda è configurata per questa valutazione.",
    "player.evaluation.continue": "Continua",
    "player.evaluation.pendingReview": "Risultato provvisorio: alcune risposte saranno corrette dal docente.",
    "player.evaluation.textToReview": "Risposta inviata: sarà corretta dal docente.",
    "share.audience.loading": "Caricamento…",
    "share.audience.serverError": "Errore del server ({{status}})",
    "share.audience.networkError": "Errore di rete",
    "share.audience.share": "Condividi",
    "share.audience.unshare": "Rimuovi la condivisione",
    "share.audience.noResult": "Nessun risultato.",
    "share.audience.allShared": "È già tutto condiviso.",
    "share.audience.members": "{{count}} membro/i",
    "share.audience.members_one": "{{count}} membro",
    "share.audience.members_other": "{{count}} membri",
    "share.audience.classShared": "{{count}} classe/i condivisa/e",
    "share.audience.groupShared": "{{count}} gruppo/i condiviso/i",
    "share.audience.serviceShared": "{{count}} reparto/i condiviso/i",
    "share.audience.titleClass": "Condividi con una classe",
    "share.audience.titleService": "Condividi con un reparto",
    "share.audience.titleGroup": "Condividi con un gruppo",
    "share.audience.noClassShared": "Nessuna classe condivisa.",
    "share.audience.noServiceShared": "Nessun reparto condiviso.",
    "share.audience.noGroupShared": "Nessun gruppo condiviso.",
    "share.audience.searchClass": "Cerca una classe…",
    "share.audience.searchService": "Cerca un reparto…",
    "share.audience.searchGroup": "Cerca un gruppo…",
    "share.audience.emptyClass": "Nessuna classe. Create delle classi nel pannello dell’istituto.",
    "share.audience.emptyService": "Nessun reparto. Create dei reparti nel pannello dell’istituto.",
    "share.audience.emptyGroup": "Nessun gruppo trovato. Create dei gruppi nel pannello dell’istituto.",
    "share.audience.assignedClass": "{{count}} studente/i aggiunto/i su {{total}} membro/i della classe «{{name}}».",
    "share.audience.assignedService": "{{count}} discente/i aggiunto/i su {{total}} membro/i del reparto «{{name}}».",
    "share.audience.assignedGroup": "{{count}} discente/i aggiunto/i su {{total}} membro/i del gruppo «{{name}}».",
    "share.audience.shareError": "Condivisione non riuscita.",
    "share.audience.unshareClassError": "Impossibile rimuovere la condivisione con la classe.",
    "share.audience.unshareGroupError": "Impossibile rimuovere la condivisione con il gruppo.",
    "profile.page.eyebrow": "Spazio personale",
    "profile.page.title": "Il mio profilo",
    "profile.page.subtitle": "Gestite in un unico posto informazioni, sicurezza e preferenze.",
    "profile.page.accountStatus": "Stato dell’account",
    "profile.page.active": "Attivo",
    "profile.page.loading": "Caricamento del profilo…",
    "profile.page.loadError": "Impossibile caricare il profilo",
    "profile.page.position": "Ruolo",
    "profile.page.position_education": "Funzione",
    "profile.page.service": "Reparto",
    "profile.page.department": "Dipartimento",
    "profile.page.location": "Località",
    "profile.page.tabProfile": "Informazioni personali",
    "profile.page.tabSecurity": "Sicurezza dell’account",
    "profile.page.tabPreferences": "Notifiche e lingua",
    "profile.page.profileHint": "Aggiornate i vostri dati di contatto e di ruolo.",
    "profile.page.profileHint_education": "Aggiornate i vostri dati di contatto.",
    "profile.page.firstName": "Nome",
    "profile.page.lastName": "Cognome",
    "profile.page.email": "E-mail",
    "profile.page.phone": "Telefono",
    "profile.page.city": "Sede / Città",
    "profile.page.city_education": "Città",
    "profile.page.country": "Paese",
    "profile.page.saving": "Salvataggio…",
    "profile.page.save": "Salva",
    "profile.page.saved": "Profilo aggiornato",
    "profile.page.saveError": "Impossibile aggiornare il profilo",
    "profile.page.securityHint": "Scegliete una password robusta e unica.",
    "profile.page.passwordMismatch": "La conferma della password non corrisponde",
    "profile.page.passwordChanged": "Password modificata",
    "profile.page.passwordError": "Impossibile modificare la password",
    "profile.page.preferencesSaved": "Preferenze salvate",
    "profile.page.preferencesError": "Impossibile salvare le preferenze",
  },
  pt: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Ação recusada: as suas permissões não permitem gerir esta conta. Contacte um administrador da instituição.",
    "institution.designers.readOnlyNotice": "Apenas consulta: a gestão de contas está reservada aos administradores da instituição.",
    "nav.learners_education": "Alunos",
    "nav.eduflowList_education": "Cursos",
    "institution.tabs.flows_education": "Cursos",
    "institution.tabs.learners_education": "Alunos",
    "institution.tabs.designers_education": "Professores",
    "institution.tabs.teachers": "Professores",
    "institution.tabs.learnerAnalytics_education": "Análise de alunos",
    "institution.nav.section.people_education": "Equipa e alunos",
    "institution.nav.section.content_education": "Conteúdos e cursos",
    "institution.rhNav.requests_education": "Pedidos de conteúdos",
    "rh.requests.title_education": "Pedidos de conteúdos",
    "rh.requests.subtitle_education": "Pedidos dos professores e validações",
    "header.trainingContentRequest": "Pedido de formação (conteúdos)",
    "header.trainingContentRequest_education": "Pedido de conteúdo",
    "dashboard.totalLearners_education": "Total de alunos",
    "dashboard.publishedTraining_education": "Cursos publicados",
    "dashboard.collaborators": "Colaboradores",
    "dashboard.collaborators_education": "Equipa pedagógica",
    "institution.dashboard.registrationCodeLabel": "Códigos de inscrição",
    "institution.dashboard.registrationCodeLearner": "Formando",
    "institution.dashboard.registrationCodeLearner_education": "Aluno",
    "institution.dashboard.registrationCodeTrainer": "Formador (professor)",
    "institution.dashboard.registrationCodeTrainer_education": "Professor",
    "institution.dashboard.registrationCodeLead": "Responsável de formação (diretor de turma)",
    "institution.dashboard.registrationCodeLead_education": "Direção",
    "institution.dashboard.registrationCodeCopy": "Copiar",
    "institution.dashboard.registrationCodeCopied": "Copiado",
    "institution.dashboard.registrationCodeHelp": "Comunique o código que corresponde ao tipo de conta. Não é possível criar uma conta sem ele.",
    "institution.flows.title_education": "Cursos",
    "institution.flows.subtitle_education": "Gira os seus cursos",
    "institution.flows.statTotal_education": "Total de cursos",
    "institution.flows.statLearners_education": "Total de alunos",
    "institution.flows.searchPh_education": "Pesquisar um curso…",
    "institution.flows.empty_education": "Nenhum curso encontrado",
    "institution.flows.badgeEnterprise_education": "Pedido de conteúdo",
    "institution.flows.deleteEnterpriseTooltip_education": "Mover para o lixo (pedido de conteúdo)",
    "institution.flows.deleteEnterpriseTitle_education": "Retirar este curso criado a partir de um pedido de conteúdo?",
    "institution.flows.deleteEnterpriseHint_education": "O curso será movido para o lixo (eliminação lógica). Os professores não podem fazê-lo; só a direção da escola pode restaurá-lo ou retirá-lo neste painel.",
    "institution.flows.deleteEnterpriseSuccess_education": "Curso movido para o lixo.",
    "role.student": "Aluno",
    "role.student_education": "Aluno",
    "role.learner_education": "Aluno",
    "role.teacher_education": "Professor",
    "institution.designers.roleTeacher_education": "Professor",
    "institution.designers.roleOptFormateur_education": "Professor",
    "institution.designers.roleTrainingLead_education": "Diretor de turma",
    "institution.designers.groupTeacher": "Formadores",
    "institution.designers.groupTeacher_education": "Professores",
    "institution.designers.groupTrainingLead": "Responsáveis de formação",
    "institution.designers.groupTrainingLead_education": "Diretores de turma",
    "institution.designers.inviteTeacher": "Convidar um professor",
    "institution.designers.inviteTeacherTitle": "Convidar um professor",
    "institution.designers.inviteTeacherHelp": "Professor: leciona nas turmas. Diretor de turma: acompanha uma turma.",
    "institution.designers.statTeachers": "Professores",
    "institution.designers.teachersSubtitle": "Professores e diretores de turma da escola.",
    "institution.learners.title_education": "Alunos",
    "institution.learners.statLearners_education": "Alunos",
    "institution.learners.subtitle_education": "{{count}} aluno(s) inscrito(s) na sua escola",
    "institution.learners.edu.courses": "Cursos",
    "institution.learners.edu.coursesDone": "Cursos concluídos",
    "institution.learners.edu.classTitle": "Turma",
    "institution.learners.edu.classHint": "A turma gere-se no separador Turmas.",
    "institution.learners.edu.noClass": "Sem turma",
    "institution.learners.edu.profileTitle": "Informações do aluno",
    "institution.learners.edu.profileHint": "Género, data de nascimento e diploma do aluno.",
    "institution.learners.edu.diploma": "Diploma obtido",
    "institution.learners.edu.saveProfile": "Guardar a ficha",
    "institution.menuBlocked.segmentBody": "Esta funcionalidade não está disponível para o seu tipo de instituição.",
    "pages.learners.sortRecentPage": "Última atividade (página atual)",
    "pages.learners.sortProgressPage": "Progresso (página atual)",
    "pages.learners.statsPageHint": "Em curso, Concluído e Progresso médio: calculados sobre as {{count}} pessoas da página atual.",
    "pages.learners.exporting": "A exportar…",
    "pages.learners.exportError": "Não foi possível exportar. Tente novamente.",
    "institution.learners.importMissingFields": "Nome, apelido e e-mail são obrigatórios.",
    "institution.learners.importBadDate": "Data ilegível: use DD/MM/AAAA ou AAAA-MM-DD.",
    "institution.learners.importNoRows": "Nenhuma linha importável encontrada.",
    "institution.learners.importResult": "{{count}} conta(s) criada(s).",
    "institution.learners.downloadTemplate": "Modelo CSV",
    "institution.classes.noAssignedFlows": "Ainda não há nenhum curso atribuído a esta turma. Escolha um acima.",
    "institution.evaluations.pointsRequired": "Introduza uma nota antes de validar.",
    "institution.evaluations.pointsRange": "A nota deve estar entre 0 e {{max}}.",
    "institution.evaluations.pointsInvalid": "Nota inválida.",
    "institution.evaluations.points": "Nota",
    "institution.evaluations.saveError": "Não foi possível guardar a nota.",
    "institution.evaluations.settingsSaveError": "Não foi possível guardar as regras.",
    "institution.evaluations.applyLevelError": "Não foi possível atualizar o nível.",
    "institution.evaluations.downloadFile": "Transferir o ficheiro",
    "institution.evaluations.fileDownloadError": "Não foi possível transferir o ficheiro.",
    "institution.learners.importDateIgnored": "{{field}} ilegível, ignorada (DD/MM/AAAA ou AAAA-MM-DD).",
    "institution.learners.importFieldBirthDate": "Data de nascimento",
    "institution.learners.importFieldHireDate": "Data de contratação",
    "institution.learners.importFieldArrivalDate": "Data de entrada",
    "institution.learners.importWarnings": "{{count}} aviso(s) (linha importada):",
    "player.evaluation.type.text": "Texto livre",
    "player.evaluation.type.singleChoice": "Escolha única",
    "player.evaluation.type.multipleChoice": "Escolha múltipla",
    "player.evaluation.type.dropdown": "Lista pendente",
    "player.evaluation.type.numeric": "Numérica",
    "player.evaluation.type.fileUpload": "Ficheiro",
    "player.evaluation.requiredAll": "Responda a todas as perguntas obrigatórias.",
    "player.evaluation.requiredOne": "Responda a esta pergunta para continuar.",
    "player.evaluation.emptyAnswer": "Resposta vazia.",
    "player.evaluation.openNoReference": "Pergunta aberta sem resposta de referência.",
    "player.evaluation.textNeedsSession": "A correção de respostas de texto requer um curso publicado e uma sessão iniciada.",
    "player.evaluation.invalidServerScore": "Resposta do servidor inválida para a classificação de texto.",
    "player.evaluation.submitError": "Erro ao enviar.",
    "player.evaluation.passReached": "Limiar de aprovação atingido.",
    "player.evaluation.passNotReached": "Limiar de aprovação não atingido.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} pts ({{threshold}} % exigido)",
    "player.evaluation.questionNumber": "Pergunta {{num}}",
    "player.evaluation.question": "Pergunta",
    "player.evaluation.untitled": "Sem título",
    "player.evaluation.toReview": "Por corrigir",
    "player.evaluation.points": "{{earned}} / {{max}} pts",
    "player.evaluation.semanticScore": "Pontuação semântica: {{score}} %",
    "player.evaluation.thanks": "Obrigado!",
    "player.evaluation.answersSaved": "As suas respostas foram guardadas.",
    "player.evaluation.questionCount": "{{count}} pergunta(s)",
    "player.evaluation.start": "Começar",
    "player.evaluation.questionProgress": "Pergunta {{num}} / {{total}}",
    "player.evaluation.required": "Obrigatória",
    "player.evaluation.chooseAnswer": "Escolha uma resposta…",
    "player.evaluation.answerPlaceholder": "Escreva a sua resposta…",
    "player.evaluation.chooseFile": "Escolher um ficheiro",
    "player.evaluation.replaceFile": "Substituir o ficheiro",
    "player.evaluation.fileHint": "PDF, imagem, documento, texto, áudio ou vídeo — máximo {{max}} MB.",
    "player.evaluation.fileTooLarge": "Ficheiro demasiado grande (máximo {{max}} MB).",
    "player.evaluation.fileBadType": "Tipo de ficheiro não aceite.",
    "player.evaluation.fileNeedsSession": "O envio de um ficheiro requer um curso publicado e uma sessão iniciada.",
    "player.evaluation.fileUploadError": "Não foi possível enviar o ficheiro. Tente novamente.",
    "player.evaluation.fileUploading": "A enviar {{name}}… {{progress}} %",
    "player.evaluation.fileSent": "Ficheiro enviado: {{name}}",
    "player.evaluation.removeFile": "Remover o ficheiro",
    "player.evaluation.fileStillUploading": "Aguarde: ainda está a ser enviado um ficheiro.",
    "player.evaluation.fileToReview": "Ficheiro enviado: será corrigido pelo professor.",
    "player.evaluation.back": "Voltar",
    "player.evaluation.grading": "A corrigir…",
    "player.evaluation.finish": "Terminar",
    "player.evaluation.next": "Seguinte",
    "player.evaluation.noQuestions": "Nenhuma pergunta está configurada para esta avaliação.",
    "player.evaluation.continue": "Continuar",
    "player.evaluation.pendingReview": "Resultado provisório: algumas respostas serão corrigidas pelo professor.",
    "player.evaluation.textToReview": "Resposta enviada: será corrigida pelo professor.",
    "share.audience.loading": "A carregar…",
    "share.audience.serverError": "Erro do servidor ({{status}})",
    "share.audience.networkError": "Erro de rede",
    "share.audience.share": "Partilhar",
    "share.audience.unshare": "Remover a partilha",
    "share.audience.noResult": "Sem resultados.",
    "share.audience.allShared": "Já está tudo partilhado.",
    "share.audience.members": "{{count}} membro(s)",
    "share.audience.members_one": "{{count}} membro",
    "share.audience.members_other": "{{count}} membros",
    "share.audience.classShared": "{{count}} turma(s) partilhada(s)",
    "share.audience.groupShared": "{{count}} grupo(s) partilhado(s)",
    "share.audience.serviceShared": "{{count}} serviço(s) partilhado(s)",
    "share.audience.titleClass": "Partilhar com uma turma",
    "share.audience.titleService": "Partilhar com um serviço",
    "share.audience.titleGroup": "Partilhar com um grupo",
    "share.audience.noClassShared": "Nenhuma turma partilhada.",
    "share.audience.noServiceShared": "Nenhum serviço partilhado.",
    "share.audience.noGroupShared": "Nenhum grupo partilhado.",
    "share.audience.searchClass": "Pesquisar uma turma…",
    "share.audience.searchService": "Pesquisar um serviço…",
    "share.audience.searchGroup": "Pesquisar um grupo…",
    "share.audience.emptyClass": "Nenhuma turma. Crie turmas no painel da instituição.",
    "share.audience.emptyService": "Nenhum serviço. Crie serviços no painel da instituição.",
    "share.audience.emptyGroup": "Nenhum grupo encontrado. Crie grupos no painel da instituição.",
    "share.audience.assignedClass": "{{count}} aluno(s) adicionado(s) de {{total}} membro(s) da turma «{{name}}».",
    "share.audience.assignedService": "{{count}} formando(s) adicionado(s) de {{total}} membro(s) do serviço «{{name}}».",
    "share.audience.assignedGroup": "{{count}} formando(s) adicionado(s) de {{total}} membro(s) do grupo «{{name}}».",
    "share.audience.shareError": "Não foi possível partilhar.",
    "share.audience.unshareClassError": "Não foi possível remover a partilha com a turma.",
    "share.audience.unshareGroupError": "Não foi possível remover a partilha com o grupo.",
    "profile.page.eyebrow": "Espaço pessoal",
    "profile.page.title": "O meu perfil",
    "profile.page.subtitle": "Centralize as suas informações, segurança e preferências.",
    "profile.page.accountStatus": "Estado da conta",
    "profile.page.active": "Ativa",
    "profile.page.loading": "A carregar o perfil…",
    "profile.page.loadError": "Não foi possível carregar o perfil",
    "profile.page.position": "Cargo",
    "profile.page.position_education": "Função",
    "profile.page.service": "Serviço",
    "profile.page.department": "Departamento",
    "profile.page.location": "Localização",
    "profile.page.tabProfile": "Informações pessoais",
    "profile.page.tabSecurity": "Segurança da conta",
    "profile.page.tabPreferences": "Notificações e idioma",
    "profile.page.profileHint": "Atualize os seus dados de contacto e de cargo.",
    "profile.page.profileHint_education": "Atualize os seus dados de contacto.",
    "profile.page.firstName": "Nome",
    "profile.page.lastName": "Apelido",
    "profile.page.email": "E-mail",
    "profile.page.phone": "Telefone",
    "profile.page.city": "Local / Cidade",
    "profile.page.city_education": "Cidade",
    "profile.page.country": "País",
    "profile.page.saving": "A guardar…",
    "profile.page.save": "Guardar",
    "profile.page.saved": "Perfil atualizado",
    "profile.page.saveError": "Não foi possível atualizar o perfil",
    "profile.page.securityHint": "Escolha uma palavra-passe forte e única.",
    "profile.page.passwordMismatch": "A confirmação da palavra-passe não corresponde",
    "profile.page.passwordChanged": "Palavra-passe alterada",
    "profile.page.passwordError": "Não foi possível alterar a palavra-passe",
    "profile.page.preferencesSaved": "Preferências guardadas",
    "profile.page.preferencesError": "Não foi possível guardar as preferências",
  },
  lb: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Aktioun refuséiert: Är Rechter erlaben et net, dëse Kont ze geréieren. Kontaktéiert en Administrateur vun der Institutioun.",
    "institution.designers.readOnlyNotice": "Nëmme consultéieren: d’Gestioun vun de Konten ass den Administrateure vun der Institutioun reservéiert.",
    "nav.learners_education": "Schüler",
    "nav.eduflowList_education": "Coursen",
    "institution.tabs.flows_education": "Coursen",
    "institution.tabs.learners_education": "Schüler",
    "institution.tabs.designers_education": "Enseignanten",
    "institution.tabs.teachers": "Enseignanten",
    "institution.tabs.learnerAnalytics_education": "Schüleranalys",
    "institution.nav.section.people_education": "Team & Schüler",
    "institution.nav.section.content_education": "Inhalter & Coursen",
    "institution.rhNav.requests_education": "Inhaltsufroen",
    "rh.requests.title_education": "Inhaltsufroen",
    "rh.requests.subtitle_education": "Ufroe vun den Enseignanten a Validatiounen",
    "header.trainingContentRequest": "Formatiounsufro (Inhalter)",
    "header.trainingContentRequest_education": "Inhaltsufro",
    "dashboard.totalLearners_education": "Schüler am Ganzen",
    "dashboard.publishedTraining_education": "Publizéiert Coursen",
    "dashboard.collaborators": "Mataarbechter",
    "dashboard.collaborators_education": "Léierpersonal",
    "institution.dashboard.registrationCodeLabel": "Umeldungscoden",
    "institution.dashboard.registrationCodeLearner": "Léierend",
    "institution.dashboard.registrationCodeLearner_education": "Schüler",
    "institution.dashboard.registrationCodeTrainer": "Formateur (Enseignant)",
    "institution.dashboard.registrationCodeTrainer_education": "Enseignant",
    "institution.dashboard.registrationCodeLead": "Formatiounsverantwortlechen (Klasseléierpersoun)",
    "institution.dashboard.registrationCodeLead_education": "Direktioun",
    "institution.dashboard.registrationCodeCopy": "Kopéieren",
    "institution.dashboard.registrationCodeCopied": "Kopéiert",
    "institution.dashboard.registrationCodeHelp": "Gitt de Code weider, deen zum Kontotyp passt. Ouni hie kann kee Kont erstallt ginn.",
    "institution.flows.title_education": "Coursen",
    "institution.flows.subtitle_education": "Geréiert Är Coursen",
    "institution.flows.statTotal_education": "Coursen am Ganzen",
    "institution.flows.statLearners_education": "Schüler am Ganzen",
    "institution.flows.searchPh_education": "Cours sichen…",
    "institution.flows.empty_education": "Kee Cours fonnt",
    "institution.flows.badgeEnterprise_education": "Inhaltsufro",
    "institution.flows.deleteEnterpriseTooltip_education": "An de Policekuerf (Inhaltsufro)",
    "institution.flows.deleteEnterpriseTitle_education": "Dëse Cours aus enger Inhaltsufro ewechhuelen?",
    "institution.flows.deleteEnterpriseHint_education": "De Cours kënnt an de Policekuerf (logescht Läschen). Enseignanten kënnen dat net; just d’Direktioun kann en hei restauréieren oder ewechhuelen.",
    "institution.flows.deleteEnterpriseSuccess_education": "Cours an de Policekuerf geréckelt.",
    "role.student": "Schüler",
    "role.student_education": "Schüler",
    "role.learner_education": "Schüler",
    "role.teacher_education": "Enseignant",
    "institution.designers.roleTeacher_education": "Enseignant",
    "institution.designers.roleOptFormateur_education": "Enseignant",
    "institution.designers.roleTrainingLead_education": "Klasseléierpersoun",
    "institution.designers.groupTeacher": "Formateuren",
    "institution.designers.groupTeacher_education": "Enseignanten",
    "institution.designers.groupTrainingLead": "Formatiounsverantwortlech",
    "institution.designers.groupTrainingLead_education": "Klasseléierpersounen",
    "institution.designers.inviteTeacher": "Enseignant invitéieren",
    "institution.designers.inviteTeacherTitle": "Enseignant invitéieren",
    "institution.designers.inviteTeacherHelp": "Enseignant: enseignéiert an de Klassen. Klasseléierpersoun: suivéiert eng Klass.",
    "institution.designers.statTeachers": "Enseignanten",
    "institution.designers.teachersSubtitle": "Enseignanten a Klasseléierpersoune vun der Schoul.",
    "institution.learners.title_education": "Schüler",
    "institution.learners.statLearners_education": "Schüler",
    "institution.learners.subtitle_education": "{{count}} Schüler an Ärer Schoul ageschriwwen",
    "institution.learners.edu.courses": "Coursen",
    "institution.learners.edu.coursesDone": "Ofgeschloss Coursen",
    "institution.learners.edu.classTitle": "Klass",
    "institution.learners.edu.classHint": "D’Klass gëtt am Tab „Klassen“ geréiert.",
    "institution.learners.edu.noClass": "Keng Klass",
    "institution.learners.edu.profileTitle": "Informatiounen zum Schüler",
    "institution.learners.edu.profileHint": "Geschlecht, Gebuertsdatum an Diplom vum Schüler.",
    "institution.learners.edu.diploma": "Kritt Diplom",
    "institution.learners.edu.saveProfile": "Fiche späicheren",
    "institution.menuBlocked.segmentBody": "Dës Funktioun ass fir Ären Typ vun Institutioun net disponibel.",
    "pages.learners.sortRecentPage": "Lescht Aktivitéit (aktuell Säit)",
    "pages.learners.sortProgressPage": "Fortschrëtt (aktuell Säit)",
    "pages.learners.statsPageHint": "Amgaang, Ofgeschloss an Duerchschnëttleche Fortschrëtt: berechent op de {{count}} Persoune vun der aktueller Säit.",
    "pages.learners.exporting": "Export leeft…",
    "pages.learners.exportError": "Export ass net gelongen. Probéiert nach eng Kéier.",
    "institution.learners.importMissingFields": "Virnumm, Numm an E-Mail sinn obligatoresch.",
    "institution.learners.importBadDate": "Datum net liesbar: benotzt DD/MM/JJJJ oder JJJJ-MM-DD.",
    "institution.learners.importNoRows": "Keng importéierbar Zeil fonnt.",
    "institution.learners.importResult": "{{count}} Kont(en) erstallt.",
    "institution.learners.downloadTemplate": "CSV-Modell",
    "institution.classes.noAssignedFlows": "Dëser Klass ass nach kee Cours zougewisen. Wielt uewen een aus.",
    "institution.evaluations.pointsRequired": "Gitt eng Note an, ier Dir validéiert.",
    "institution.evaluations.pointsRange": "D’Note muss tëscht 0 an {{max}} leien.",
    "institution.evaluations.pointsInvalid": "Ongëlteg Note.",
    "institution.evaluations.points": "Note",
    "institution.evaluations.saveError": "D’Note konnt net gespäichert ginn.",
    "institution.evaluations.settingsSaveError": "D’Reegele konnten net gespäichert ginn.",
    "institution.evaluations.applyLevelError": "De Niveau konnt net aktualiséiert ginn.",
    "institution.evaluations.downloadFile": "Fichier eroflueden",
    "institution.evaluations.fileDownloadError": "De Fichier konnt net erofgeluede ginn.",
    "institution.learners.importDateIgnored": "{{field}} net liesbar, ignoréiert (DD/MM/JJJJ oder JJJJ-MM-DD).",
    "institution.learners.importFieldBirthDate": "Gebuertsdatum",
    "institution.learners.importFieldHireDate": "Astellungsdatum",
    "institution.learners.importFieldArrivalDate": "Ufanksdatum",
    "institution.learners.importWarnings": "{{count}} Warnung(en) (Zeil importéiert):",
    "player.evaluation.type.text": "Fräien Text",
    "player.evaluation.type.singleChoice": "Eenzel Auswiel",
    "player.evaluation.type.multipleChoice": "Méifach Auswiel",
    "player.evaluation.type.dropdown": "Auswiellëscht",
    "player.evaluation.type.numeric": "Numeresch",
    "player.evaluation.type.fileUpload": "Fichier",
    "player.evaluation.requiredAll": "Äntwert w.e.g. op all obligatoresch Froen.",
    "player.evaluation.requiredOne": "Äntwert w.e.g. op dës Fro fir weiderzefueren.",
    "player.evaluation.emptyAnswer": "Eidel Äntwert.",
    "player.evaluation.openNoReference": "Oppe Fro ouni Referenzäntwert.",
    "player.evaluation.textNeedsSession": "D’Korrektur vun Textäntwerten brauch e publizéierte Cours an eng ugemellt Sessioun.",
    "player.evaluation.invalidServerScore": "Ongëlteg Serveräntwert fir d’Textbewäertung.",
    "player.evaluation.submitError": "Feeler beim Schécken.",
    "player.evaluation.passReached": "Bestanensgrenz erreecht.",
    "player.evaluation.passNotReached": "Bestanensgrenz net erreecht.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} Pkt. ({{threshold}} % néideg)",
    "player.evaluation.questionNumber": "Fro {{num}}",
    "player.evaluation.question": "Fro",
    "player.evaluation.untitled": "Ouni Titel",
    "player.evaluation.toReview": "Ze korrigéieren",
    "player.evaluation.points": "{{earned}} / {{max}} Pkt.",
    "player.evaluation.semanticScore": "Semanteschen Score: {{score}} %",
    "player.evaluation.thanks": "Merci!",
    "player.evaluation.answersSaved": "Är Äntwerte goufe gespäichert.",
    "player.evaluation.questionCount": "{{count}} Fro(en)",
    "player.evaluation.start": "Ufänken",
    "player.evaluation.questionProgress": "Fro {{num}} / {{total}}",
    "player.evaluation.required": "Obligatoresch",
    "player.evaluation.chooseAnswer": "Äntwert wielen…",
    "player.evaluation.answerPlaceholder": "Gitt Är Äntwert an…",
    "player.evaluation.chooseFile": "Fichier auswielen",
    "player.evaluation.replaceFile": "Fichier ersetzen",
    "player.evaluation.fileHint": "PDF, Bild, Dokument, Text, Audio oder Video — maximal {{max}} MB.",
    "player.evaluation.fileTooLarge": "Fichier ze grouss (maximal {{max}} MB).",
    "player.evaluation.fileBadType": "Fichierstyp net erlaabt.",
    "player.evaluation.fileNeedsSession": "Fir e Fichier eropzelueden brauch et e publizéierte Cours an eng ugemellt Sessioun.",
    "player.evaluation.fileUploadError": "De Fichier konnt net eropgeluede ginn. Probéiert nach eng Kéier.",
    "player.evaluation.fileUploading": "{{name}} gëtt eropgelueden… {{progress}} %",
    "player.evaluation.fileSent": "Fichier geschéckt: {{name}}",
    "player.evaluation.removeFile": "Fichier ewechhuelen",
    "player.evaluation.fileStillUploading": "Waart w.e.g.: e Fichier gëtt nach eropgelueden.",
    "player.evaluation.fileToReview": "Fichier iwwermëttelt: en gëtt vum Enseignant korrigéiert.",
    "player.evaluation.back": "Zréck",
    "player.evaluation.grading": "Korrektur…",
    "player.evaluation.finish": "Ofschléissen",
    "player.evaluation.next": "Weider",
    "player.evaluation.noQuestions": "Fir dës Bewäertung ass keng Fro agestallt.",
    "player.evaluation.continue": "Weider",
    "player.evaluation.pendingReview": "Provisorescht Resultat: verschidden Äntwerte gi vum Enseignant korrigéiert.",
    "player.evaluation.textToReview": "Äntwert iwwermëttelt: si gëtt vum Enseignant korrigéiert.",
    "share.audience.loading": "Gëtt gelueden…",
    "share.audience.serverError": "Serverfeeler ({{status}})",
    "share.audience.networkError": "Netzwierkfeeler",
    "share.audience.share": "Deelen",
    "share.audience.unshare": "Deelen ophiewen",
    "share.audience.noResult": "Keng Resultater.",
    "share.audience.allShared": "Alles ass scho gedeelt.",
    "share.audience.members": "{{count}} Member(en)",
    "share.audience.members_one": "{{count}} Member",
    "share.audience.members_other": "{{count}} Memberen",
    "share.audience.classShared": "{{count}} Klass(en) gedeelt",
    "share.audience.groupShared": "{{count}} Grupp(en) gedeelt",
    "share.audience.serviceShared": "{{count}} Service(en) gedeelt",
    "share.audience.titleClass": "Mat enger Klass deelen",
    "share.audience.titleService": "Mat engem Service deelen",
    "share.audience.titleGroup": "Mat enger Grupp deelen",
    "share.audience.noClassShared": "Keng Klass gedeelt.",
    "share.audience.noServiceShared": "Kee Service gedeelt.",
    "share.audience.noGroupShared": "Keng Grupp gedeelt.",
    "share.audience.searchClass": "Klass sichen…",
    "share.audience.searchService": "Service sichen…",
    "share.audience.searchGroup": "Grupp sichen…",
    "share.audience.emptyClass": "Keng Klass. Leet Klassen am Tableau de bord vun der Institutioun un.",
    "share.audience.emptyService": "Kee Service. Leet Servicer am Tableau de bord vun der Institutioun un.",
    "share.audience.emptyGroup": "Keng Grupp fonnt. Leet Gruppen am Tableau de bord vun der Institutioun un.",
    "share.audience.assignedClass": "{{count}} Schüler vun {{total}} Member(en) vun der Klass „{{name}}“ derbäigesat.",
    "share.audience.assignedService": "{{count}} Léierend vun {{total}} Member(en) vum Service „{{name}}“ derbäigesat.",
    "share.audience.assignedGroup": "{{count}} Léierend vun {{total}} Member(en) vun der Grupp „{{name}}“ derbäigesat.",
    "share.audience.shareError": "Deelen ass net gelongen.",
    "share.audience.unshareClassError": "D’Deele mat der Klass konnt net opgehuewe ginn.",
    "share.audience.unshareGroupError": "D’Deele mat der Grupp konnt net opgehuewe ginn.",
    "profile.page.eyebrow": "Perséinleche Beräich",
    "profile.page.title": "Mäi Profil",
    "profile.page.subtitle": "Verwalt Är Informatiounen, Är Sécherheet an Är Astellungen op enger Plaz.",
    "profile.page.accountStatus": "Kontostatus",
    "profile.page.active": "Aktiv",
    "profile.page.loading": "Profil gëtt gelueden…",
    "profile.page.loadError": "De Profil konnt net geluede ginn",
    "profile.page.position": "Posten",
    "profile.page.position_education": "Funktioun",
    "profile.page.service": "Service",
    "profile.page.department": "Departement",
    "profile.page.location": "Standuert",
    "profile.page.tabProfile": "Perséinlech Informatiounen",
    "profile.page.tabSecurity": "Kontosécherheet",
    "profile.page.tabPreferences": "Notifikatiounen & Sprooch",
    "profile.page.profileHint": "Aktualiséiert Är Kontakt- a Postendaten.",
    "profile.page.profileHint_education": "Aktualiséiert Är Kontaktdaten.",
    "profile.page.firstName": "Virnumm",
    "profile.page.lastName": "Numm",
    "profile.page.email": "E-Mail",
    "profile.page.phone": "Telefon",
    "profile.page.city": "Site / Stad",
    "profile.page.city_education": "Stad",
    "profile.page.country": "Land",
    "profile.page.saving": "Gëtt gespäichert…",
    "profile.page.save": "Späicheren",
    "profile.page.saved": "Profil aktualiséiert",
    "profile.page.saveError": "De Profil konnt net aktualiséiert ginn",
    "profile.page.securityHint": "Wielt e staarkt, eenzegaartegt Passwuert.",
    "profile.page.passwordMismatch": "D’Bestätegung vum Passwuert stëmmt net",
    "profile.page.passwordChanged": "Passwuert geännert",
    "profile.page.passwordError": "D’Passwuert konnt net geännert ginn",
    "profile.page.preferencesSaved": "Astellunge gespäichert",
    "profile.page.preferencesError": "D’Astellunge konnten net gespäichert ginn",
  },
  uk: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Дію відхилено: ваші права не дозволяють керувати цим обліковим записом. Зверніться до адміністратора установи.",
    "institution.designers.readOnlyNotice": "Лише перегляд: керування обліковими записами доступне тільки адміністраторам установи.",
    "nav.learners_education": "Учні",
    "nav.eduflowList_education": "Курси",
    "institution.tabs.flows_education": "Курси",
    "institution.tabs.learners_education": "Учні",
    "institution.tabs.designers_education": "Вчителі",
    "institution.tabs.teachers": "Вчителі",
    "institution.tabs.learnerAnalytics_education": "Аналітика учнів",
    "institution.nav.section.people_education": "Колектив і учні",
    "institution.nav.section.content_education": "Матеріали та курси",
    "institution.rhNav.requests_education": "Запити на матеріали",
    "rh.requests.title_education": "Запити на матеріали",
    "rh.requests.subtitle_education": "Запити вчителів і погодження",
    "header.trainingContentRequest": "Запит на навчання (матеріали)",
    "header.trainingContentRequest_education": "Запит на матеріал",
    "dashboard.totalLearners_education": "Усього учнів",
    "dashboard.publishedTraining_education": "Опубліковані курси",
    "dashboard.collaborators": "Співробітники",
    "dashboard.collaborators_education": "Педагогічний колектив",
    "institution.dashboard.registrationCodeLabel": "Коди реєстрації",
    "institution.dashboard.registrationCodeLearner": "Слухач",
    "institution.dashboard.registrationCodeLearner_education": "Учень",
    "institution.dashboard.registrationCodeTrainer": "Тренер (викладач)",
    "institution.dashboard.registrationCodeTrainer_education": "Вчитель",
    "institution.dashboard.registrationCodeLead": "Відповідальний за навчання (класний керівник)",
    "institution.dashboard.registrationCodeLead_education": "Адміністрація",
    "institution.dashboard.registrationCodeCopy": "Копіювати",
    "institution.dashboard.registrationCodeCopied": "Скопійовано",
    "institution.dashboard.registrationCodeHelp": "Повідомте код, що відповідає типу облікового запису. Без нього обліковий запис не створити.",
    "institution.flows.title_education": "Курси",
    "institution.flows.subtitle_education": "Керуйте своїми курсами",
    "institution.flows.statTotal_education": "Усього курсів",
    "institution.flows.statLearners_education": "Усього учнів",
    "institution.flows.searchPh_education": "Шукати курс…",
    "institution.flows.empty_education": "Курсів не знайдено",
    "institution.flows.badgeEnterprise_education": "Запит на матеріал",
    "institution.flows.deleteEnterpriseTooltip_education": "До кошика (запит на матеріал)",
    "institution.flows.deleteEnterpriseTitle_education": "Прибрати цей курс, створений за запитом?",
    "institution.flows.deleteEnterpriseHint_education": "Курс буде переміщено до кошика (логічне видалення). Вчителі не можуть цього зробити; лише адміністрація закладу може відновити або прибрати його тут.",
    "institution.flows.deleteEnterpriseSuccess_education": "Курс переміщено до кошика.",
    "role.student": "Учень",
    "role.student_education": "Учень",
    "role.learner_education": "Учень",
    "role.teacher_education": "Вчитель",
    "institution.designers.roleTeacher_education": "Вчитель",
    "institution.designers.roleOptFormateur_education": "Вчитель",
    "institution.designers.roleTrainingLead_education": "Класний керівник",
    "institution.designers.groupTeacher": "Тренери",
    "institution.designers.groupTeacher_education": "Вчителі",
    "institution.designers.groupTrainingLead": "Відповідальні за навчання",
    "institution.designers.groupTrainingLead_education": "Класні керівники",
    "institution.designers.inviteTeacher": "Запросити вчителя",
    "institution.designers.inviteTeacherTitle": "Запросити вчителя",
    "institution.designers.inviteTeacherHelp": "Вчитель: викладає в класах. Класний керівник: супроводжує клас.",
    "institution.designers.statTeachers": "Вчителі",
    "institution.designers.teachersSubtitle": "Вчителі та класні керівники закладу.",
    "institution.learners.title_education": "Учні",
    "institution.learners.statLearners_education": "Учні",
    "institution.learners.subtitle_education": "Учнів у закладі: {{count}}",
    "institution.learners.edu.courses": "Курси",
    "institution.learners.edu.coursesDone": "Завершені курси",
    "institution.learners.edu.classTitle": "Клас",
    "institution.learners.edu.classHint": "Клас налаштовується на вкладці «Класи».",
    "institution.learners.edu.noClass": "Без класу",
    "institution.learners.edu.profileTitle": "Відомості про учня",
    "institution.learners.edu.profileHint": "Стать, дата народження та документ про освіту учня.",
    "institution.learners.edu.diploma": "Отриманий документ",
    "institution.learners.edu.saveProfile": "Зберегти картку",
    "institution.menuBlocked.segmentBody": "Ця функція недоступна для вашого типу закладу.",
    "pages.learners.sortRecentPage": "Остання активність (поточна сторінка)",
    "pages.learners.sortProgressPage": "Прогрес (поточна сторінка)",
    "pages.learners.statsPageHint": "«У процесі», «Завершено» і «Середній прогрес» розраховано для {{count}} осіб на поточній сторінці.",
    "pages.learners.exporting": "Експорт…",
    "pages.learners.exportError": "Не вдалося експортувати. Спробуйте ще раз.",
    "institution.learners.importMissingFields": "Ім’я, прізвище та e-mail обов’язкові.",
    "institution.learners.importBadDate": "Нечитабельна дата: використовуйте ДД/ММ/РРРР або РРРР-ММ-ДД.",
    "institution.learners.importNoRows": "Не знайдено рядків для імпорту.",
    "institution.learners.importResult": "Створено облікових записів: {{count}}.",
    "institution.learners.downloadTemplate": "Шаблон CSV",
    "institution.classes.noAssignedFlows": "Цьому класу ще не призначено жодного курсу. Виберіть курс вище.",
    "institution.evaluations.pointsRequired": "Введіть оцінку перед підтвердженням.",
    "institution.evaluations.pointsRange": "Оцінка має бути від 0 до {{max}}.",
    "institution.evaluations.pointsInvalid": "Недійсна оцінка.",
    "institution.evaluations.points": "Оцінка",
    "institution.evaluations.saveError": "Не вдалося зберегти оцінку.",
    "institution.evaluations.settingsSaveError": "Не вдалося зберегти правила.",
    "institution.evaluations.applyLevelError": "Не вдалося оновити рівень.",
    "institution.evaluations.downloadFile": "Завантажити файл",
    "institution.evaluations.fileDownloadError": "Не вдалося завантажити файл.",
    "institution.learners.importDateIgnored": "{{field}}: нечитабельне значення пропущено (ДД/ММ/РРРР або РРРР-ММ-ДД).",
    "institution.learners.importFieldBirthDate": "Дата народження",
    "institution.learners.importFieldHireDate": "Дата прийому",
    "institution.learners.importFieldArrivalDate": "Дата приходу",
    "institution.learners.importWarnings": "Попереджень: {{count}} (рядок імпортовано):",
    "player.evaluation.type.text": "Вільний текст",
    "player.evaluation.type.singleChoice": "Один варіант",
    "player.evaluation.type.multipleChoice": "Кілька варіантів",
    "player.evaluation.type.dropdown": "Випадний список",
    "player.evaluation.type.numeric": "Числова",
    "player.evaluation.type.fileUpload": "Файл",
    "player.evaluation.requiredAll": "Дайте відповідь на всі обов’язкові запитання.",
    "player.evaluation.requiredOne": "Дайте відповідь на це запитання, щоб продовжити.",
    "player.evaluation.emptyAnswer": "Порожня відповідь.",
    "player.evaluation.openNoReference": "Відкрите запитання без еталонної відповіді.",
    "player.evaluation.textNeedsSession": "Перевірка текстових відповідей потребує опублікованого курсу та активного входу.",
    "player.evaluation.invalidServerScore": "Недійсна відповідь сервера для оцінювання тексту.",
    "player.evaluation.submitError": "Помилка під час надсилання.",
    "player.evaluation.passReached": "Прохідний поріг досягнуто.",
    "player.evaluation.passNotReached": "Прохідний поріг не досягнуто.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} б. (потрібно {{threshold}} %)",
    "player.evaluation.questionNumber": "Запитання {{num}}",
    "player.evaluation.question": "Запитання",
    "player.evaluation.untitled": "Без назви",
    "player.evaluation.toReview": "На перевірці",
    "player.evaluation.points": "{{earned}} / {{max}} б.",
    "player.evaluation.semanticScore": "Семантична оцінка: {{score}} %",
    "player.evaluation.thanks": "Дякуємо!",
    "player.evaluation.answersSaved": "Ваші відповіді збережено.",
    "player.evaluation.questionCount": "Запитань: {{count}}",
    "player.evaluation.start": "Почати",
    "player.evaluation.questionProgress": "Запитання {{num}} / {{total}}",
    "player.evaluation.required": "Обов’язково",
    "player.evaluation.chooseAnswer": "Виберіть відповідь…",
    "player.evaluation.answerPlaceholder": "Введіть свою відповідь…",
    "player.evaluation.chooseFile": "Виберіть файл",
    "player.evaluation.replaceFile": "Замінити файл",
    "player.evaluation.fileHint": "PDF, зображення, документ, текст, аудіо чи відео — до {{max}} МБ.",
    "player.evaluation.fileTooLarge": "Файл завеликий (до {{max}} МБ).",
    "player.evaluation.fileBadType": "Тип файлу не підтримується.",
    "player.evaluation.fileNeedsSession": "Для надсилання файлу потрібні опублікований курс і вхід у систему.",
    "player.evaluation.fileUploadError": "Не вдалося надіслати файл. Спробуйте ще раз.",
    "player.evaluation.fileUploading": "Надсилання {{name}}… {{progress}} %",
    "player.evaluation.fileSent": "Файл надіслано: {{name}}",
    "player.evaluation.removeFile": "Прибрати файл",
    "player.evaluation.fileStillUploading": "Зачекайте: файл ще надсилається.",
    "player.evaluation.fileToReview": "Файл надіслано: його перевірить викладач.",
    "player.evaluation.back": "Назад",
    "player.evaluation.grading": "Перевірка…",
    "player.evaluation.finish": "Завершити",
    "player.evaluation.next": "Далі",
    "player.evaluation.noQuestions": "Для цього оцінювання не налаштовано запитань.",
    "player.evaluation.continue": "Продовжити",
    "player.evaluation.pendingReview": "Попередній результат: деякі відповіді перевірить викладач.",
    "player.evaluation.textToReview": "Відповідь надіслано: її перевірить викладач.",
    "share.audience.loading": "Завантаження…",
    "share.audience.serverError": "Помилка сервера ({{status}})",
    "share.audience.networkError": "Помилка мережі",
    "share.audience.share": "Поділитися",
    "share.audience.unshare": "Скасувати доступ",
    "share.audience.noResult": "Немає результатів.",
    "share.audience.allShared": "Усе вже надано.",
    "share.audience.members": "Учасників: {{count}}",
    "share.audience.classShared": "Надано класам: {{count}}",
    "share.audience.groupShared": "Надано групам: {{count}}",
    "share.audience.serviceShared": "Надано відділам: {{count}}",
    "share.audience.titleClass": "Надати класу",
    "share.audience.titleService": "Надати відділу",
    "share.audience.titleGroup": "Надати групі",
    "share.audience.noClassShared": "Жодному класу не надано.",
    "share.audience.noServiceShared": "Жодному відділу не надано.",
    "share.audience.noGroupShared": "Жодній групі не надано.",
    "share.audience.searchClass": "Шукати клас…",
    "share.audience.searchService": "Шукати відділ…",
    "share.audience.searchGroup": "Шукати групу…",
    "share.audience.emptyClass": "Немає класів. Створіть класи на панелі закладу.",
    "share.audience.emptyService": "Немає відділів. Створіть відділи на панелі закладу.",
    "share.audience.emptyGroup": "Груп не знайдено. Створіть групи на панелі закладу.",
    "share.audience.assignedClass": "Додано учнів: {{count}} з {{total}} у класі «{{name}}».",
    "share.audience.assignedService": "Додано слухачів: {{count}} з {{total}} у відділі «{{name}}».",
    "share.audience.assignedGroup": "Додано слухачів: {{count}} з {{total}} у групі «{{name}}».",
    "share.audience.shareError": "Не вдалося надати доступ.",
    "share.audience.unshareClassError": "Не вдалося скасувати доступ класу.",
    "share.audience.unshareGroupError": "Не вдалося скасувати доступ групи.",
    "profile.page.eyebrow": "Особистий простір",
    "profile.page.title": "Мій профіль",
    "profile.page.subtitle": "Керуйте даними, безпекою та налаштуваннями в одному місці.",
    "profile.page.accountStatus": "Стан облікового запису",
    "profile.page.active": "Активний",
    "profile.page.loading": "Завантаження профілю…",
    "profile.page.loadError": "Не вдалося завантажити профіль",
    "profile.page.position": "Посада",
    "profile.page.position_education": "Функція",
    "profile.page.service": "Відділ",
    "profile.page.department": "Підрозділ",
    "profile.page.location": "Розташування",
    "profile.page.tabProfile": "Особисті дані",
    "profile.page.tabSecurity": "Безпека облікового запису",
    "profile.page.tabPreferences": "Сповіщення та мова",
    "profile.page.profileHint": "Оновіть контактні дані та посаду.",
    "profile.page.profileHint_education": "Оновіть контактні дані.",
    "profile.page.firstName": "Ім’я",
    "profile.page.lastName": "Прізвище",
    "profile.page.email": "E-mail",
    "profile.page.phone": "Телефон",
    "profile.page.city": "Майданчик / Місто",
    "profile.page.city_education": "Місто",
    "profile.page.country": "Країна",
    "profile.page.saving": "Збереження…",
    "profile.page.save": "Зберегти",
    "profile.page.saved": "Профіль оновлено",
    "profile.page.saveError": "Не вдалося оновити профіль",
    "profile.page.securityHint": "Оберіть надійний унікальний пароль.",
    "profile.page.passwordMismatch": "Підтвердження пароля не збігається",
    "profile.page.passwordChanged": "Пароль змінено",
    "profile.page.passwordError": "Не вдалося змінити пароль",
    "profile.page.preferencesSaved": "Налаштування збережено",
    "profile.page.preferencesError": "Не вдалося зберегти налаштування",
  },
  ru: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "Действие отклонено: ваши права не позволяют управлять этой учётной записью. Обратитесь к администратору организации.",
    "institution.designers.readOnlyNotice": "Только просмотр: управление учётными записями доступно только администраторам организации.",
    "nav.learners_education": "Ученики",
    "nav.eduflowList_education": "Курсы",
    "institution.tabs.flows_education": "Курсы",
    "institution.tabs.learners_education": "Ученики",
    "institution.tabs.designers_education": "Учителя",
    "institution.tabs.teachers": "Учителя",
    "institution.tabs.learnerAnalytics_education": "Аналитика учеников",
    "institution.nav.section.people_education": "Коллектив и ученики",
    "institution.nav.section.content_education": "Материалы и курсы",
    "institution.rhNav.requests_education": "Запросы на материалы",
    "rh.requests.title_education": "Запросы на материалы",
    "rh.requests.subtitle_education": "Запросы учителей и согласования",
    "header.trainingContentRequest": "Запрос на обучение (материалы)",
    "header.trainingContentRequest_education": "Запрос на материал",
    "dashboard.totalLearners_education": "Всего учеников",
    "dashboard.publishedTraining_education": "Опубликованные курсы",
    "dashboard.collaborators": "Сотрудники",
    "dashboard.collaborators_education": "Педагогический коллектив",
    "institution.dashboard.registrationCodeLabel": "Коды регистрации",
    "institution.dashboard.registrationCodeLearner": "Слушатель",
    "institution.dashboard.registrationCodeLearner_education": "Ученик",
    "institution.dashboard.registrationCodeTrainer": "Тренер (преподаватель)",
    "institution.dashboard.registrationCodeTrainer_education": "Учитель",
    "institution.dashboard.registrationCodeLead": "Ответственный за обучение (классный руководитель)",
    "institution.dashboard.registrationCodeLead_education": "Администрация",
    "institution.dashboard.registrationCodeCopy": "Копировать",
    "institution.dashboard.registrationCodeCopied": "Скопировано",
    "institution.dashboard.registrationCodeHelp": "Сообщите код, соответствующий типу учётной записи. Без него учётную запись не создать.",
    "institution.flows.title_education": "Курсы",
    "institution.flows.subtitle_education": "Управляйте своими курсами",
    "institution.flows.statTotal_education": "Всего курсов",
    "institution.flows.statLearners_education": "Всего учеников",
    "institution.flows.searchPh_education": "Найти курс…",
    "institution.flows.empty_education": "Курсы не найдены",
    "institution.flows.badgeEnterprise_education": "Запрос на материал",
    "institution.flows.deleteEnterpriseTooltip_education": "В корзину (запрос на материал)",
    "institution.flows.deleteEnterpriseTitle_education": "Убрать этот курс, созданный по запросу?",
    "institution.flows.deleteEnterpriseHint_education": "Курс будет перемещён в корзину (логическое удаление). Учителя не могут этого сделать; только администрация школы может восстановить или убрать его здесь.",
    "institution.flows.deleteEnterpriseSuccess_education": "Курс перемещён в корзину.",
    "role.student": "Ученик",
    "role.student_education": "Ученик",
    "role.learner_education": "Ученик",
    "role.teacher_education": "Учитель",
    "institution.designers.roleTeacher_education": "Учитель",
    "institution.designers.roleOptFormateur_education": "Учитель",
    "institution.designers.roleTrainingLead_education": "Классный руководитель",
    "institution.designers.groupTeacher": "Тренеры",
    "institution.designers.groupTeacher_education": "Учителя",
    "institution.designers.groupTrainingLead": "Ответственные за обучение",
    "institution.designers.groupTrainingLead_education": "Классные руководители",
    "institution.designers.inviteTeacher": "Пригласить учителя",
    "institution.designers.inviteTeacherTitle": "Пригласить учителя",
    "institution.designers.inviteTeacherHelp": "Учитель: преподаёт в классах. Классный руководитель: ведёт класс.",
    "institution.designers.statTeachers": "Учителя",
    "institution.designers.teachersSubtitle": "Учителя и классные руководители школы.",
    "institution.learners.title_education": "Ученики",
    "institution.learners.statLearners_education": "Ученики",
    "institution.learners.subtitle_education": "Учеников в школе: {{count}}",
    "institution.learners.edu.courses": "Курсы",
    "institution.learners.edu.coursesDone": "Завершённые курсы",
    "institution.learners.edu.classTitle": "Класс",
    "institution.learners.edu.classHint": "Класс настраивается на вкладке «Классы».",
    "institution.learners.edu.noClass": "Без класса",
    "institution.learners.edu.profileTitle": "Сведения об ученике",
    "institution.learners.edu.profileHint": "Пол, дата рождения и документ об образовании ученика.",
    "institution.learners.edu.diploma": "Полученный документ",
    "institution.learners.edu.saveProfile": "Сохранить карточку",
    "institution.menuBlocked.segmentBody": "Эта функция недоступна для вашего типа организации.",
    "pages.learners.sortRecentPage": "Последняя активность (текущая страница)",
    "pages.learners.sortProgressPage": "Прогресс (текущая страница)",
    "pages.learners.statsPageHint": "«В процессе», «Завершено» и «Средний прогресс» рассчитаны для {{count}} человек на текущей странице.",
    "pages.learners.exporting": "Экспорт…",
    "pages.learners.exportError": "Не удалось экспортировать. Повторите попытку.",
    "institution.learners.importMissingFields": "Имя, фамилия и e-mail обязательны.",
    "institution.learners.importBadDate": "Нечитаемая дата: используйте ДД/ММ/ГГГГ или ГГГГ-ММ-ДД.",
    "institution.learners.importNoRows": "Не найдено строк для импорта.",
    "institution.learners.importResult": "Создано учётных записей: {{count}}.",
    "institution.learners.downloadTemplate": "Шаблон CSV",
    "institution.classes.noAssignedFlows": "Этому классу ещё не назначен ни один курс. Выберите курс выше.",
    "institution.evaluations.pointsRequired": "Введите оценку перед подтверждением.",
    "institution.evaluations.pointsRange": "Оценка должна быть от 0 до {{max}}.",
    "institution.evaluations.pointsInvalid": "Недопустимая оценка.",
    "institution.evaluations.points": "Оценка",
    "institution.evaluations.saveError": "Не удалось сохранить оценку.",
    "institution.evaluations.settingsSaveError": "Не удалось сохранить правила.",
    "institution.evaluations.applyLevelError": "Не удалось обновить уровень.",
    "institution.evaluations.downloadFile": "Скачать файл",
    "institution.evaluations.fileDownloadError": "Не удалось скачать файл.",
    "institution.learners.importDateIgnored": "{{field}}: нечитаемое значение пропущено (ДД/ММ/ГГГГ или ГГГГ-ММ-ДД).",
    "institution.learners.importFieldBirthDate": "Дата рождения",
    "institution.learners.importFieldHireDate": "Дата найма",
    "institution.learners.importFieldArrivalDate": "Дата прихода",
    "institution.learners.importWarnings": "Предупреждений: {{count}} (строка импортирована):",
    "player.evaluation.type.text": "Свободный текст",
    "player.evaluation.type.singleChoice": "Один вариант",
    "player.evaluation.type.multipleChoice": "Несколько вариантов",
    "player.evaluation.type.dropdown": "Выпадающий список",
    "player.evaluation.type.numeric": "Числовой",
    "player.evaluation.type.fileUpload": "Файл",
    "player.evaluation.requiredAll": "Ответьте на все обязательные вопросы.",
    "player.evaluation.requiredOne": "Ответьте на этот вопрос, чтобы продолжить.",
    "player.evaluation.emptyAnswer": "Пустой ответ.",
    "player.evaluation.openNoReference": "Открытый вопрос без эталонного ответа.",
    "player.evaluation.textNeedsSession": "Проверка текстовых ответов требует опубликованного курса и выполненного входа.",
    "player.evaluation.invalidServerScore": "Недопустимый ответ сервера при оценке текста.",
    "player.evaluation.submitError": "Ошибка при отправке.",
    "player.evaluation.passReached": "Проходной порог достигнут.",
    "player.evaluation.passNotReached": "Проходной порог не достигнут.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} б. (нужно {{threshold}} %)",
    "player.evaluation.questionNumber": "Вопрос {{num}}",
    "player.evaluation.question": "Вопрос",
    "player.evaluation.untitled": "Без названия",
    "player.evaluation.toReview": "На проверке",
    "player.evaluation.points": "{{earned}} / {{max}} б.",
    "player.evaluation.semanticScore": "Семантическая оценка: {{score}} %",
    "player.evaluation.thanks": "Спасибо!",
    "player.evaluation.answersSaved": "Ваши ответы сохранены.",
    "player.evaluation.questionCount": "Вопросов: {{count}}",
    "player.evaluation.start": "Начать",
    "player.evaluation.questionProgress": "Вопрос {{num}} / {{total}}",
    "player.evaluation.required": "Обязательно",
    "player.evaluation.chooseAnswer": "Выберите ответ…",
    "player.evaluation.answerPlaceholder": "Введите ответ…",
    "player.evaluation.chooseFile": "Выберите файл",
    "player.evaluation.replaceFile": "Заменить файл",
    "player.evaluation.fileHint": "PDF, изображение, документ, текст, аудио или видео — до {{max}} МБ.",
    "player.evaluation.fileTooLarge": "Файл слишком большой (до {{max}} МБ).",
    "player.evaluation.fileBadType": "Тип файла не поддерживается.",
    "player.evaluation.fileNeedsSession": "Для отправки файла нужны опубликованный курс и выполненный вход.",
    "player.evaluation.fileUploadError": "Не удалось отправить файл. Повторите попытку.",
    "player.evaluation.fileUploading": "Отправка {{name}}… {{progress}} %",
    "player.evaluation.fileSent": "Файл отправлен: {{name}}",
    "player.evaluation.removeFile": "Убрать файл",
    "player.evaluation.fileStillUploading": "Подождите: файл ещё отправляется.",
    "player.evaluation.fileToReview": "Файл отправлен: его проверит преподаватель.",
    "player.evaluation.back": "Назад",
    "player.evaluation.grading": "Проверка…",
    "player.evaluation.finish": "Завершить",
    "player.evaluation.next": "Далее",
    "player.evaluation.noQuestions": "Для этой оценки не настроено вопросов.",
    "player.evaluation.continue": "Продолжить",
    "player.evaluation.pendingReview": "Предварительный результат: некоторые ответы проверит преподаватель.",
    "player.evaluation.textToReview": "Ответ отправлен: его проверит преподаватель.",
    "share.audience.loading": "Загрузка…",
    "share.audience.serverError": "Ошибка сервера ({{status}})",
    "share.audience.networkError": "Ошибка сети",
    "share.audience.share": "Поделиться",
    "share.audience.unshare": "Отменить доступ",
    "share.audience.noResult": "Нет результатов.",
    "share.audience.allShared": "Всё уже предоставлено.",
    "share.audience.members": "Участников: {{count}}",
    "share.audience.classShared": "Предоставлено классам: {{count}}",
    "share.audience.groupShared": "Предоставлено группам: {{count}}",
    "share.audience.serviceShared": "Предоставлено отделам: {{count}}",
    "share.audience.titleClass": "Предоставить классу",
    "share.audience.titleService": "Предоставить отделу",
    "share.audience.titleGroup": "Предоставить группе",
    "share.audience.noClassShared": "Ни одному классу не предоставлено.",
    "share.audience.noServiceShared": "Ни одному отделу не предоставлено.",
    "share.audience.noGroupShared": "Ни одной группе не предоставлено.",
    "share.audience.searchClass": "Найти класс…",
    "share.audience.searchService": "Найти отдел…",
    "share.audience.searchGroup": "Найти группу…",
    "share.audience.emptyClass": "Нет классов. Создайте классы на панели организации.",
    "share.audience.emptyService": "Нет отделов. Создайте отделы на панели организации.",
    "share.audience.emptyGroup": "Группы не найдены. Создайте группы на панели организации.",
    "share.audience.assignedClass": "Добавлено учеников: {{count}} из {{total}} в классе «{{name}}».",
    "share.audience.assignedService": "Добавлено слушателей: {{count}} из {{total}} в отделе «{{name}}».",
    "share.audience.assignedGroup": "Добавлено слушателей: {{count}} из {{total}} в группе «{{name}}».",
    "share.audience.shareError": "Не удалось предоставить доступ.",
    "share.audience.unshareClassError": "Не удалось отменить доступ класса.",
    "share.audience.unshareGroupError": "Не удалось отменить доступ группы.",
    "profile.page.eyebrow": "Личное пространство",
    "profile.page.title": "Мой профиль",
    "profile.page.subtitle": "Управляйте данными, безопасностью и настройками в одном месте.",
    "profile.page.accountStatus": "Состояние учётной записи",
    "profile.page.active": "Активна",
    "profile.page.loading": "Загрузка профиля…",
    "profile.page.loadError": "Не удалось загрузить профиль",
    "profile.page.position": "Должность",
    "profile.page.position_education": "Функция",
    "profile.page.service": "Отдел",
    "profile.page.department": "Подразделение",
    "profile.page.location": "Местоположение",
    "profile.page.tabProfile": "Личные данные",
    "profile.page.tabSecurity": "Безопасность учётной записи",
    "profile.page.tabPreferences": "Уведомления и язык",
    "profile.page.profileHint": "Обновите контактные данные и должность.",
    "profile.page.profileHint_education": "Обновите контактные данные.",
    "profile.page.firstName": "Имя",
    "profile.page.lastName": "Фамилия",
    "profile.page.email": "E-mail",
    "profile.page.phone": "Телефон",
    "profile.page.city": "Площадка / Город",
    "profile.page.city_education": "Город",
    "profile.page.country": "Страна",
    "profile.page.saving": "Сохранение…",
    "profile.page.save": "Сохранить",
    "profile.page.saved": "Профиль обновлён",
    "profile.page.saveError": "Не удалось обновить профиль",
    "profile.page.securityHint": "Выберите надёжный уникальный пароль.",
    "profile.page.passwordMismatch": "Подтверждение пароля не совпадает",
    "profile.page.passwordChanged": "Пароль изменён",
    "profile.page.passwordError": "Не удалось изменить пароль",
    "profile.page.preferencesSaved": "Настройки сохранены",
    "profile.page.preferencesError": "Не удалось сохранить настройки",
  },
  ar: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "تم رفض الإجراء: لا تسمح لك صلاحياتك بإدارة هذا الحساب. تواصل مع أحد مديري المؤسسة.",
    "institution.designers.readOnlyNotice": "عرض فقط: إدارة الحسابات مقتصرة على مديري المؤسسة.",
    "nav.learners_education": "الطلاب",
    "nav.eduflowList_education": "الدروس",
    "institution.tabs.flows_education": "الدروس",
    "institution.tabs.learners_education": "الطلاب",
    "institution.tabs.designers_education": "المعلمون",
    "institution.tabs.teachers": "المعلمون",
    "institution.tabs.learnerAnalytics_education": "تحليل الطلاب",
    "institution.nav.section.people_education": "الفريق والطلاب",
    "institution.nav.section.content_education": "المحتوى والدروس",
    "institution.rhNav.requests_education": "طلبات المحتوى",
    "rh.requests.title_education": "طلبات المحتوى",
    "rh.requests.subtitle_education": "طلبات المعلمين والموافقات",
    "header.trainingContentRequest": "طلب تدريب (محتوى)",
    "header.trainingContentRequest_education": "طلب محتوى",
    "dashboard.totalLearners_education": "إجمالي الطلاب",
    "dashboard.publishedTraining_education": "الدروس المنشورة",
    "dashboard.collaborators": "المتعاونون",
    "dashboard.collaborators_education": "الطاقم التعليمي",
    "institution.dashboard.registrationCodeLabel": "رموز التسجيل",
    "institution.dashboard.registrationCodeLearner": "متعلم",
    "institution.dashboard.registrationCodeLearner_education": "طالب",
    "institution.dashboard.registrationCodeTrainer": "مدرب (معلم)",
    "institution.dashboard.registrationCodeTrainer_education": "معلم",
    "institution.dashboard.registrationCodeLead": "مسؤول التدريب (المعلم الرئيسي)",
    "institution.dashboard.registrationCodeLead_education": "الإدارة",
    "institution.dashboard.registrationCodeCopy": "نسخ",
    "institution.dashboard.registrationCodeCopied": "تم النسخ",
    "institution.dashboard.registrationCodeHelp": "شارك الرمز المطابق لنوع الحساب. لا يمكن إنشاء حساب بدونه.",
    "institution.flows.title_education": "الدروس",
    "institution.flows.subtitle_education": "أدر دروسك",
    "institution.flows.statTotal_education": "إجمالي الدروس",
    "institution.flows.statLearners_education": "إجمالي الطلاب",
    "institution.flows.searchPh_education": "ابحث عن درس…",
    "institution.flows.empty_education": "لم يتم العثور على أي درس",
    "institution.flows.badgeEnterprise_education": "طلب محتوى",
    "institution.flows.deleteEnterpriseTooltip_education": "نقل إلى سلة المهملات (طلب محتوى)",
    "institution.flows.deleteEnterpriseTitle_education": "إزالة هذا الدرس الناتج عن طلب محتوى؟",
    "institution.flows.deleteEnterpriseHint_education": "سيُنقل الدرس إلى سلة المهملات (حذف منطقي). لا يمكن للمعلمين القيام بذلك؛ يمكن لإدارة المؤسسة وحدها استعادته أو إزالته من هذه اللوحة.",
    "institution.flows.deleteEnterpriseSuccess_education": "نُقل الدرس إلى سلة المهملات.",
    "role.student": "طالب",
    "role.student_education": "طالب",
    "role.learner_education": "طالب",
    "role.teacher_education": "معلم",
    "institution.designers.roleTeacher_education": "معلم",
    "institution.designers.roleOptFormateur_education": "معلم",
    "institution.designers.roleTrainingLead_education": "المعلم الرئيسي",
    "institution.designers.groupTeacher": "المدربون",
    "institution.designers.groupTeacher_education": "المعلمون",
    "institution.designers.groupTrainingLead": "مسؤولو التدريب",
    "institution.designers.groupTrainingLead_education": "المعلمون الرئيسيون",
    "institution.designers.inviteTeacher": "دعوة معلم",
    "institution.designers.inviteTeacherTitle": "دعوة معلم",
    "institution.designers.inviteTeacherHelp": "المعلم: يدرّس في الفصول. المعلم الرئيسي: يتابع فصلاً.",
    "institution.designers.statTeachers": "المعلمون",
    "institution.designers.teachersSubtitle": "معلمو المؤسسة ومعلموها الرئيسيون.",
    "institution.learners.title_education": "الطلاب",
    "institution.learners.statLearners_education": "الطلاب",
    "institution.learners.subtitle_education": "عدد الطلاب المسجلين في مؤسستك: {{count}}",
    "institution.learners.edu.courses": "الدروس",
    "institution.learners.edu.coursesDone": "الدروس المكتملة",
    "institution.learners.edu.classTitle": "الفصل",
    "institution.learners.edu.classHint": "يُدار الفصل من تبويب الفصول.",
    "institution.learners.edu.noClass": "بلا فصل",
    "institution.learners.edu.profileTitle": "معلومات الطالب",
    "institution.learners.edu.profileHint": "جنس الطالب وتاريخ ميلاده وشهادته.",
    "institution.learners.edu.diploma": "الشهادة المحصّلة",
    "institution.learners.edu.saveProfile": "حفظ البطاقة",
    "institution.menuBlocked.segmentBody": "هذه الميزة غير متاحة لنوع مؤسستك.",
    "pages.learners.sortRecentPage": "آخر نشاط (الصفحة الحالية)",
    "pages.learners.sortProgressPage": "التقدم (الصفحة الحالية)",
    "pages.learners.statsPageHint": "«قيد التقدم» و«مكتمل» و«متوسط التقدم» محسوبة على {{count}} شخصاً في الصفحة الحالية.",
    "pages.learners.exporting": "جارٍ التصدير…",
    "pages.learners.exportError": "تعذّر التصدير. حاول مرة أخرى.",
    "institution.learners.importMissingFields": "الاسم واللقب والبريد الإلكتروني إلزامية.",
    "institution.learners.importBadDate": "تاريخ غير مقروء: استخدم يوم/شهر/سنة أو سنة-شهر-يوم.",
    "institution.learners.importNoRows": "لم يتم العثور على أي سطر قابل للاستيراد.",
    "institution.learners.importResult": "عدد الحسابات المُنشأة: {{count}}.",
    "institution.learners.downloadTemplate": "قالب CSV",
    "institution.classes.noAssignedFlows": "لم يُسند أي درس إلى هذا الفصل بعد. اختر درساً أعلاه.",
    "institution.evaluations.pointsRequired": "أدخل علامة قبل التحقق.",
    "institution.evaluations.pointsRange": "يجب أن تكون العلامة بين 0 و{{max}}.",
    "institution.evaluations.pointsInvalid": "علامة غير صالحة.",
    "institution.evaluations.points": "العلامة",
    "institution.evaluations.saveError": "تعذّر حفظ العلامة.",
    "institution.evaluations.settingsSaveError": "تعذّر حفظ القواعد.",
    "institution.evaluations.applyLevelError": "تعذّر تحديث المستوى.",
    "institution.evaluations.downloadFile": "تنزيل الملف",
    "institution.evaluations.fileDownloadError": "تعذّر تنزيل الملف.",
    "institution.learners.importDateIgnored": "{{field}} غير مقروء، تم تجاهله (يوم/شهر/سنة أو سنة-شهر-يوم).",
    "institution.learners.importFieldBirthDate": "تاريخ الميلاد",
    "institution.learners.importFieldHireDate": "تاريخ التوظيف",
    "institution.learners.importFieldArrivalDate": "تاريخ الالتحاق",
    "institution.learners.importWarnings": "عدد التحذيرات: {{count}} (تم استيراد السطر):",
    "player.evaluation.type.text": "نص حر",
    "player.evaluation.type.singleChoice": "اختيار واحد",
    "player.evaluation.type.multipleChoice": "اختيارات متعددة",
    "player.evaluation.type.dropdown": "قائمة منسدلة",
    "player.evaluation.type.numeric": "رقمي",
    "player.evaluation.type.fileUpload": "ملف",
    "player.evaluation.requiredAll": "يرجى الإجابة عن جميع الأسئلة الإلزامية.",
    "player.evaluation.requiredOne": "يرجى الإجابة عن هذا السؤال للمتابعة.",
    "player.evaluation.emptyAnswer": "إجابة فارغة.",
    "player.evaluation.openNoReference": "سؤال مفتوح بلا إجابة مرجعية.",
    "player.evaluation.textNeedsSession": "يتطلب تصحيح الإجابات النصية درساً منشوراً وجلسة مسجّلة الدخول.",
    "player.evaluation.invalidServerScore": "استجابة خادم غير صالحة لتقييم النص.",
    "player.evaluation.submitError": "خطأ أثناء الإرسال.",
    "player.evaluation.passReached": "تم بلوغ حد النجاح.",
    "player.evaluation.passNotReached": "لم يتم بلوغ حد النجاح.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} نقطة (المطلوب {{threshold}}٪)",
    "player.evaluation.questionNumber": "السؤال {{num}}",
    "player.evaluation.question": "سؤال",
    "player.evaluation.untitled": "بلا عنوان",
    "player.evaluation.toReview": "قيد التصحيح",
    "player.evaluation.points": "{{earned}} / {{max}} نقطة",
    "player.evaluation.semanticScore": "الدرجة الدلالية: {{score}}٪",
    "player.evaluation.thanks": "شكراً!",
    "player.evaluation.answersSaved": "تم حفظ إجاباتك.",
    "player.evaluation.questionCount": "عدد الأسئلة: {{count}}",
    "player.evaluation.start": "ابدأ",
    "player.evaluation.questionProgress": "السؤال {{num}} / {{total}}",
    "player.evaluation.required": "إلزامي",
    "player.evaluation.chooseAnswer": "اختر إجابة…",
    "player.evaluation.answerPlaceholder": "اكتب إجابتك…",
    "player.evaluation.chooseFile": "اختر ملفاً",
    "player.evaluation.replaceFile": "استبدال الملف",
    "player.evaluation.fileHint": "PDF أو صورة أو مستند أو نص أو صوت أو فيديو — بحد أقصى {{max}} م.ب.",
    "player.evaluation.fileTooLarge": "الملف كبير جداً (الحد الأقصى {{max}} م.ب).",
    "player.evaluation.fileBadType": "نوع الملف غير مقبول.",
    "player.evaluation.fileNeedsSession": "يتطلب رفع ملف درساً منشوراً وجلسة مسجّلة الدخول.",
    "player.evaluation.fileUploadError": "تعذّر رفع الملف. حاول مرة أخرى.",
    "player.evaluation.fileUploading": "جارٍ رفع {{name}}… {{progress}}٪",
    "player.evaluation.fileSent": "تم إرسال الملف: {{name}}",
    "player.evaluation.removeFile": "إزالة الملف",
    "player.evaluation.fileStillUploading": "يرجى الانتظار: لا يزال ملف قيد الرفع.",
    "player.evaluation.fileToReview": "تم إرسال الملف: سيصححه المعلم.",
    "player.evaluation.back": "رجوع",
    "player.evaluation.grading": "جارٍ التصحيح…",
    "player.evaluation.finish": "إنهاء",
    "player.evaluation.next": "التالي",
    "player.evaluation.noQuestions": "لا توجد أسئلة مُعدّة لهذا التقييم.",
    "player.evaluation.continue": "متابعة",
    "player.evaluation.pendingReview": "نتيجة مؤقتة: سيصحح المعلم بعض الإجابات.",
    "player.evaluation.textToReview": "تم إرسال الإجابة: سيصححها المعلم.",
    "share.audience.loading": "جارٍ التحميل…",
    "share.audience.serverError": "خطأ في الخادم ({{status}})",
    "share.audience.networkError": "خطأ في الشبكة",
    "share.audience.share": "مشاركة",
    "share.audience.unshare": "إلغاء المشاركة",
    "share.audience.noResult": "لا توجد نتائج.",
    "share.audience.allShared": "تمت مشاركة كل شيء بالفعل.",
    "share.audience.members": "عدد الأعضاء: {{count}}",
    "share.audience.classShared": "عدد الفصول المشاركة: {{count}}",
    "share.audience.groupShared": "عدد المجموعات المشاركة: {{count}}",
    "share.audience.serviceShared": "عدد الأقسام المشاركة: {{count}}",
    "share.audience.titleClass": "مشاركة مع فصل",
    "share.audience.titleService": "مشاركة مع قسم",
    "share.audience.titleGroup": "مشاركة مع مجموعة",
    "share.audience.noClassShared": "لا توجد فصول مشاركة.",
    "share.audience.noServiceShared": "لا توجد أقسام مشاركة.",
    "share.audience.noGroupShared": "لا توجد مجموعات مشاركة.",
    "share.audience.searchClass": "ابحث عن فصل…",
    "share.audience.searchService": "ابحث عن قسم…",
    "share.audience.searchGroup": "ابحث عن مجموعة…",
    "share.audience.emptyClass": "لا توجد فصول. أنشئ فصولاً من لوحة المؤسسة.",
    "share.audience.emptyService": "لا توجد أقسام. أنشئ أقساماً من لوحة المؤسسة.",
    "share.audience.emptyGroup": "لم يتم العثور على مجموعات. أنشئ مجموعات من لوحة المؤسسة.",
    "share.audience.assignedClass": "تمت إضافة {{count}} طالب من أصل {{total}} عضو في الفصل «{{name}}».",
    "share.audience.assignedService": "تمت إضافة {{count}} متعلم من أصل {{total}} عضو في القسم «{{name}}».",
    "share.audience.assignedGroup": "تمت إضافة {{count}} متعلم من أصل {{total}} عضو في المجموعة «{{name}}».",
    "share.audience.shareError": "تعذّرت المشاركة.",
    "share.audience.unshareClassError": "تعذّر إلغاء المشاركة مع الفصل.",
    "share.audience.unshareGroupError": "تعذّر إلغاء المشاركة مع المجموعة.",
    "profile.page.eyebrow": "المساحة الشخصية",
    "profile.page.title": "ملفي الشخصي",
    "profile.page.subtitle": "اجمع معلوماتك وأمانك وتفضيلاتك في مكان واحد.",
    "profile.page.accountStatus": "حالة الحساب",
    "profile.page.active": "نشط",
    "profile.page.loading": "جارٍ تحميل الملف الشخصي…",
    "profile.page.loadError": "تعذّر تحميل الملف الشخصي",
    "profile.page.position": "المنصب",
    "profile.page.position_education": "الوظيفة",
    "profile.page.service": "القسم",
    "profile.page.department": "الإدارة",
    "profile.page.location": "الموقع",
    "profile.page.tabProfile": "المعلومات الشخصية",
    "profile.page.tabSecurity": "أمان الحساب",
    "profile.page.tabPreferences": "الإشعارات واللغة",
    "profile.page.profileHint": "حدّث معلومات الاتصال والمنصب.",
    "profile.page.profileHint_education": "حدّث معلومات الاتصال.",
    "profile.page.firstName": "الاسم",
    "profile.page.lastName": "اللقب",
    "profile.page.email": "البريد الإلكتروني",
    "profile.page.phone": "الهاتف",
    "profile.page.city": "الموقع / المدينة",
    "profile.page.city_education": "المدينة",
    "profile.page.country": "البلد",
    "profile.page.saving": "جارٍ الحفظ…",
    "profile.page.save": "حفظ",
    "profile.page.saved": "تم تحديث الملف الشخصي",
    "profile.page.saveError": "تعذّر تحديث الملف الشخصي",
    "profile.page.securityHint": "اختر كلمة مرور قوية وفريدة.",
    "profile.page.passwordMismatch": "تأكيد كلمة المرور غير مطابق",
    "profile.page.passwordChanged": "تم تغيير كلمة المرور",
    "profile.page.passwordError": "تعذّر تغيير كلمة المرور",
    "profile.page.preferencesSaved": "تم حفظ التفضيلات",
    "profile.page.preferencesError": "تعذّر حفظ التفضيلات",
  },
  tr: {
    // Gestion des comptes (onglet Collaborateurs / Professeurs) : refus serveur et consultation seule.
    "institution.designers.actionForbidden": "İşlem reddedildi: yetkileriniz bu hesabı yönetmenize izin vermiyor. Kurumun bir yöneticisiyle iletişime geçin.",
    "institution.designers.readOnlyNotice": "Yalnızca görüntüleme: hesap yönetimi kurum yöneticilerine ayrılmıştır.",
    "nav.learners_education": "Öğrenciler",
    "nav.eduflowList_education": "Dersler",
    "institution.tabs.flows_education": "Dersler",
    "institution.tabs.learners_education": "Öğrenciler",
    "institution.tabs.designers_education": "Öğretmenler",
    "institution.tabs.teachers": "Öğretmenler",
    "institution.tabs.learnerAnalytics_education": "Öğrenci analizi",
    "institution.nav.section.people_education": "Kadro ve öğrenciler",
    "institution.nav.section.content_education": "İçerik ve dersler",
    "institution.rhNav.requests_education": "İçerik talepleri",
    "rh.requests.title_education": "İçerik talepleri",
    "rh.requests.subtitle_education": "Öğretmen talepleri ve onaylar",
    "header.trainingContentRequest": "Eğitim talebi (içerik)",
    "header.trainingContentRequest_education": "İçerik talebi",
    "dashboard.totalLearners_education": "Toplam öğrenci",
    "dashboard.publishedTraining_education": "Yayınlanan dersler",
    "dashboard.collaborators": "Çalışanlar",
    "dashboard.collaborators_education": "Öğretim kadrosu",
    "institution.dashboard.registrationCodeLabel": "Kayıt kodları",
    "institution.dashboard.registrationCodeLearner": "Katılımcı",
    "institution.dashboard.registrationCodeLearner_education": "Öğrenci",
    "institution.dashboard.registrationCodeTrainer": "Eğitmen (öğretmen)",
    "institution.dashboard.registrationCodeTrainer_education": "Öğretmen",
    "institution.dashboard.registrationCodeLead": "Eğitim sorumlusu (sınıf öğretmeni)",
    "institution.dashboard.registrationCodeLead_education": "Okul yönetimi",
    "institution.dashboard.registrationCodeCopy": "Kopyala",
    "institution.dashboard.registrationCodeCopied": "Kopyalandı",
    "institution.dashboard.registrationCodeHelp": "Hesap türüne uygun kodu paylaşın. Kod olmadan hesap oluşturulamaz.",
    "institution.flows.title_education": "Dersler",
    "institution.flows.subtitle_education": "Derslerinizi yönetin",
    "institution.flows.statTotal_education": "Toplam ders",
    "institution.flows.statLearners_education": "Toplam öğrenci",
    "institution.flows.searchPh_education": "Ders ara…",
    "institution.flows.empty_education": "Ders bulunamadı",
    "institution.flows.badgeEnterprise_education": "İçerik talebi",
    "institution.flows.deleteEnterpriseTooltip_education": "Çöp kutusuna taşı (içerik talebi)",
    "institution.flows.deleteEnterpriseTitle_education": "İçerik talebinden oluşturulan bu ders kaldırılsın mı?",
    "institution.flows.deleteEnterpriseHint_education": "Ders çöp kutusuna taşınacak (mantıksal silme). Öğretmenler bunu yapamaz; yalnızca okul yönetimi bu panelden geri yükleyebilir veya kaldırabilir.",
    "institution.flows.deleteEnterpriseSuccess_education": "Ders çöp kutusuna taşındı.",
    "role.student": "Öğrenci",
    "role.student_education": "Öğrenci",
    "role.learner_education": "Öğrenci",
    "role.teacher_education": "Öğretmen",
    "institution.designers.roleTeacher_education": "Öğretmen",
    "institution.designers.roleOptFormateur_education": "Öğretmen",
    "institution.designers.roleTrainingLead_education": "Sınıf öğretmeni",
    "institution.designers.groupTeacher": "Eğitmenler",
    "institution.designers.groupTeacher_education": "Öğretmenler",
    "institution.designers.groupTrainingLead": "Eğitim sorumluları",
    "institution.designers.groupTrainingLead_education": "Sınıf öğretmenleri",
    "institution.designers.inviteTeacher": "Öğretmen davet et",
    "institution.designers.inviteTeacherTitle": "Öğretmen davet et",
    "institution.designers.inviteTeacherHelp": "Öğretmen: sınıflarda ders verir. Sınıf öğretmeni: bir sınıfı takip eder.",
    "institution.designers.statTeachers": "Öğretmenler",
    "institution.designers.teachersSubtitle": "Okulun öğretmenleri ve sınıf öğretmenleri.",
    "institution.learners.title_education": "Öğrenciler",
    "institution.learners.statLearners_education": "Öğrenciler",
    "institution.learners.subtitle_education": "Okulunuza kayıtlı {{count}} öğrenci",
    "institution.learners.edu.courses": "Dersler",
    "institution.learners.edu.coursesDone": "Tamamlanan dersler",
    "institution.learners.edu.classTitle": "Sınıf",
    "institution.learners.edu.classHint": "Sınıf, Sınıflar sekmesinden yönetilir.",
    "institution.learners.edu.noClass": "Sınıf yok",
    "institution.learners.edu.profileTitle": "Öğrenci bilgileri",
    "institution.learners.edu.profileHint": "Öğrencinin cinsiyeti, doğum tarihi ve diploması.",
    "institution.learners.edu.diploma": "Alınan diploma",
    "institution.learners.edu.saveProfile": "Kaydı kaydet",
    "institution.menuBlocked.segmentBody": "Bu özellik kurum türünüz için sunulmuyor.",
    "pages.learners.sortRecentPage": "Son etkinlik (geçerli sayfa)",
    "pages.learners.sortProgressPage": "İlerleme (geçerli sayfa)",
    "pages.learners.statsPageHint": "Devam eden, Tamamlanan ve Ort. ilerleme: geçerli sayfadaki {{count}} kişi üzerinden hesaplanır.",
    "pages.learners.exporting": "Dışa aktarılıyor…",
    "pages.learners.exportError": "Dışa aktarılamadı. Lütfen tekrar deneyin.",
    "institution.learners.importMissingFields": "Ad, soyad ve e-posta zorunludur.",
    "institution.learners.importBadDate": "Okunamayan tarih: GG/AA/YYYY veya YYYY-AA-GG kullanın.",
    "institution.learners.importNoRows": "İçe aktarılabilir satır bulunamadı.",
    "institution.learners.importResult": "{{count}} hesap oluşturuldu.",
    "institution.learners.downloadTemplate": "CSV şablonu",
    "institution.classes.noAssignedFlows": "Bu sınıfa henüz ders atanmadı. Yukarıdan bir ders seçin.",
    "institution.evaluations.pointsRequired": "Onaylamadan önce bir not girin.",
    "institution.evaluations.pointsRange": "Not 0 ile {{max}} arasında olmalıdır.",
    "institution.evaluations.pointsInvalid": "Geçersiz not.",
    "institution.evaluations.points": "Not",
    "institution.evaluations.saveError": "Not kaydedilemedi.",
    "institution.evaluations.settingsSaveError": "Kurallar kaydedilemedi.",
    "institution.evaluations.applyLevelError": "Seviye güncellenemedi.",
    "institution.evaluations.downloadFile": "Dosyayı indir",
    "institution.evaluations.fileDownloadError": "Dosya indirilemedi.",
    "institution.learners.importDateIgnored": "{{field}} okunamadı, yok sayıldı (GG/AA/YYYY veya YYYY-AA-GG).",
    "institution.learners.importFieldBirthDate": "Doğum tarihi",
    "institution.learners.importFieldHireDate": "İşe giriş tarihi",
    "institution.learners.importFieldArrivalDate": "Başlangıç tarihi",
    "institution.learners.importWarnings": "{{count}} uyarı (satır içe aktarıldı):",
    "player.evaluation.type.text": "Serbest metin",
    "player.evaluation.type.singleChoice": "Tek seçim",
    "player.evaluation.type.multipleChoice": "Çoklu seçim",
    "player.evaluation.type.dropdown": "Açılır liste",
    "player.evaluation.type.numeric": "Sayısal",
    "player.evaluation.type.fileUpload": "Dosya",
    "player.evaluation.requiredAll": "Lütfen tüm zorunlu soruları yanıtlayın.",
    "player.evaluation.requiredOne": "Devam etmek için bu soruyu yanıtlayın.",
    "player.evaluation.emptyAnswer": "Boş yanıt.",
    "player.evaluation.openNoReference": "Referans yanıtı olmayan açık soru.",
    "player.evaluation.textNeedsSession": "Metin yanıtlarının değerlendirilmesi yayınlanmış bir ders ve oturum açılmış olmasını gerektirir.",
    "player.evaluation.invalidServerScore": "Metin puanlaması için geçersiz sunucu yanıtı.",
    "player.evaluation.submitError": "Gönderme sırasında hata.",
    "player.evaluation.passReached": "Geçme eşiğine ulaşıldı.",
    "player.evaluation.passNotReached": "Geçme eşiğine ulaşılamadı.",
    "player.evaluation.scoreSummary": "{{earned}} / {{max}} puan (%{{threshold}} gerekli)",
    "player.evaluation.questionNumber": "Soru {{num}}",
    "player.evaluation.question": "Soru",
    "player.evaluation.untitled": "Başlıksız",
    "player.evaluation.toReview": "Değerlendirilecek",
    "player.evaluation.points": "{{earned}} / {{max}} puan",
    "player.evaluation.semanticScore": "Anlamsal puan: %{{score}}",
    "player.evaluation.thanks": "Teşekkürler!",
    "player.evaluation.answersSaved": "Yanıtlarınız kaydedildi.",
    "player.evaluation.questionCount": "{{count}} soru",
    "player.evaluation.start": "Başla",
    "player.evaluation.questionProgress": "Soru {{num}} / {{total}}",
    "player.evaluation.required": "Zorunlu",
    "player.evaluation.chooseAnswer": "Bir yanıt seçin…",
    "player.evaluation.answerPlaceholder": "Yanıtınızı yazın…",
    "player.evaluation.chooseFile": "Dosya seçin",
    "player.evaluation.replaceFile": "Dosyayı değiştir",
    "player.evaluation.fileHint": "PDF, görsel, belge, metin, ses veya video — en fazla {{max}} MB.",
    "player.evaluation.fileTooLarge": "Dosya çok büyük (en fazla {{max}} MB).",
    "player.evaluation.fileBadType": "Dosya türü kabul edilmiyor.",
    "player.evaluation.fileNeedsSession": "Dosya yüklemek için yayınlanmış bir ders ve oturum açılmış olması gerekir.",
    "player.evaluation.fileUploadError": "Dosya yüklenemedi. Lütfen tekrar deneyin.",
    "player.evaluation.fileUploading": "{{name}} yükleniyor… %{{progress}}",
    "player.evaluation.fileSent": "Dosya gönderildi: {{name}}",
    "player.evaluation.removeFile": "Dosyayı kaldır",
    "player.evaluation.fileStillUploading": "Lütfen bekleyin: bir dosya hâlâ yükleniyor.",
    "player.evaluation.fileToReview": "Dosya gönderildi: öğretmen tarafından değerlendirilecek.",
    "player.evaluation.back": "Geri",
    "player.evaluation.grading": "Değerlendiriliyor…",
    "player.evaluation.finish": "Bitir",
    "player.evaluation.next": "İleri",
    "player.evaluation.noQuestions": "Bu değerlendirme için soru yapılandırılmamış.",
    "player.evaluation.continue": "Devam et",
    "player.evaluation.pendingReview": "Geçici sonuç: bazı yanıtlar öğretmen tarafından değerlendirilecek.",
    "player.evaluation.textToReview": "Yanıt gönderildi: öğretmen tarafından değerlendirilecek.",
    "share.audience.loading": "Yükleniyor…",
    "share.audience.serverError": "Sunucu hatası ({{status}})",
    "share.audience.networkError": "Ağ hatası",
    "share.audience.share": "Paylaş",
    "share.audience.unshare": "Paylaşımı kaldır",
    "share.audience.noResult": "Sonuç yok.",
    "share.audience.allShared": "Her şey zaten paylaşıldı.",
    "share.audience.members": "{{count}} üye",
    "share.audience.classShared": "{{count}} sınıf paylaşıldı",
    "share.audience.groupShared": "{{count}} grup paylaşıldı",
    "share.audience.serviceShared": "{{count}} departman paylaşıldı",
    "share.audience.titleClass": "Bir sınıfla paylaş",
    "share.audience.titleService": "Bir departmanla paylaş",
    "share.audience.titleGroup": "Bir grupla paylaş",
    "share.audience.noClassShared": "Paylaşılan sınıf yok.",
    "share.audience.noServiceShared": "Paylaşılan departman yok.",
    "share.audience.noGroupShared": "Paylaşılan grup yok.",
    "share.audience.searchClass": "Sınıf ara…",
    "share.audience.searchService": "Departman ara…",
    "share.audience.searchGroup": "Grup ara…",
    "share.audience.emptyClass": "Sınıf yok. Kurum panelinden sınıf oluşturun.",
    "share.audience.emptyService": "Departman yok. Kurum panelinden departman oluşturun.",
    "share.audience.emptyGroup": "Grup bulunamadı. Kurum panelinden grup oluşturun.",
    "share.audience.assignedClass": "«{{name}}» sınıfının {{total}} üyesinden {{count}} öğrenci eklendi.",
    "share.audience.assignedService": "«{{name}}» departmanının {{total}} üyesinden {{count}} katılımcı eklendi.",
    "share.audience.assignedGroup": "«{{name}}» grubunun {{total}} üyesinden {{count}} katılımcı eklendi.",
    "share.audience.shareError": "Paylaşılamadı.",
    "share.audience.unshareClassError": "Sınıf paylaşımı kaldırılamadı.",
    "share.audience.unshareGroupError": "Grup paylaşımı kaldırılamadı.",
    "profile.page.eyebrow": "Kişisel alan",
    "profile.page.title": "Profilim",
    "profile.page.subtitle": "Bilgilerinizi, güvenliğinizi ve tercihlerinizi tek yerden yönetin.",
    "profile.page.accountStatus": "Hesap durumu",
    "profile.page.active": "Etkin",
    "profile.page.loading": "Profil yükleniyor…",
    "profile.page.loadError": "Profil yüklenemedi",
    "profile.page.position": "Pozisyon",
    "profile.page.position_education": "Görev",
    "profile.page.service": "Departman",
    "profile.page.department": "Bölüm",
    "profile.page.location": "Konum",
    "profile.page.tabProfile": "Kişisel bilgiler",
    "profile.page.tabSecurity": "Hesap güvenliği",
    "profile.page.tabPreferences": "Bildirimler ve dil",
    "profile.page.profileHint": "İletişim ve pozisyon bilgilerinizi güncelleyin.",
    "profile.page.profileHint_education": "İletişim bilgilerinizi güncelleyin.",
    "profile.page.firstName": "Ad",
    "profile.page.lastName": "Soyad",
    "profile.page.email": "E-posta",
    "profile.page.phone": "Telefon",
    "profile.page.city": "Yerleşke / Şehir",
    "profile.page.city_education": "Şehir",
    "profile.page.country": "Ülke",
    "profile.page.saving": "Kaydediliyor…",
    "profile.page.save": "Kaydet",
    "profile.page.saved": "Profil güncellendi",
    "profile.page.saveError": "Profil güncellenemedi",
    "profile.page.securityHint": "Güçlü ve benzersiz bir parola seçin.",
    "profile.page.passwordMismatch": "Parola onayı eşleşmiyor",
    "profile.page.passwordChanged": "Parola değiştirildi",
    "profile.page.passwordError": "Parola değiştirilemedi",
    "profile.page.preferencesSaved": "Tercihler kaydedildi",
    "profile.page.preferencesError": "Tercihler kaydedilemedi",
  },
};
